var m = require('mithril');
var prop = require('mithril/stream');
var go = require('gojs');
var Promise = require('bluebird');
var RealtimeDragSelectingTool = require('./diagram/RealtimeDragSelectingTool');
var edit = require('../../layout/components/edit');
var diagramConfiguration = require('../components/diagram/diagramConfiguration');
var modal = require('../../layout/components/modal');
var loaders = require('../../layout/components/loaders');
var deletePartModal = require('./diagram/deletePartModal');
var equipmentModel = require('../models/equipment');
var measurableModel = require('../../measurable/models/measurable');
var serviceTaxonomy = require('../../service/serviceTaxonomy');
var portConnectionRules = require('./diagram/portConnectionRules');
var createNodeTemplates = require('./diagram/createNodeTemplates');
var createLinkTemplates = require('./diagram/createLinkTemplates');
var gridEquipmentNode = require('./diagram/equipmentTemplates/helpers/gridEquipmentNode');
var pointSubscriber = require('../../components/pointSubscriberClass');
var customEquimentModel = require('../../../../../models/equipment/customEquipment');
var ontologyFields = require('../../../../../models/equipment/ontologyFields');
var GroupingTool = require('./diagram/groupingTool');

var pastingData = false;
// var legend = require('./diagram/legend');


var equipmentDiagram = {};
const controlsEdgeColor = '#FFFFFF';
const diagramAutoLayout = {
  clusterGapX: 60,
  clusterGapY: 60,
  bottomRightGapX: 80,
  bottomRightGapY: 80,
  minimumClusterWidth: 260,
  datasetTileWidth: 300,
  datasetTileGapX: 40,
  datasetTileGapY: 28,
  equipmentDatasetGapY: 28
};
const externalFlagLayout = {
  gapFromEquipment: 56,
  laneGap: 16,
  verticalGap: 14,
  horizontalGap: 24,
  minWidth: 140,
  height: 44,
  maxPerLane: 6,
  maxLanes: 4
};
const gridShellPortLayout = {
  defaultCols: 7,
  defaultRows: 5,
  defaultWidth: 180,
  defaultHeight: 120
};
const roomGridDefaults = {
  cols: 90,
  rows: 20
};
const maxSafeDiagramCoordinate = 1000000;
const maxSafeGridCellCount = 10000;

function cloneJson(value) {
  if (typeof value === 'undefined') return undefined;
  return JSON.parse(JSON.stringify(value));
}

function isSafeDiagramCoordinate(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && Math.abs(numericValue) <= maxSafeDiagramCoordinate;
}

function readSafePositiveDimension(value, maxValue) {
  const numericValue = Number(value);
  const upperLimit = Number.isFinite(maxValue) ? maxValue : maxSafeDiagramCoordinate;
  return Number.isFinite(numericValue) && numericValue > 0 && numericValue <= upperLimit
    ? numericValue
    : null;
}

function normalizeDiagramModelKey(value) {
  if (value === undefined || value === null) return null;
  if (typeof value === 'object' && value['@rid']) return value['@rid'].toString();
  return value.toString();
}

function getDiagramModelNodeKeySet(ctrl) {
  return new Set([
    ctrl.groupsArray,
    ctrl.nodeEquipmentArray,
    ctrl.nodePortArray,
    ctrl.nodeDirectDatasetArray,
    ctrl.nodeDatasetArray,
    ctrl.nodeLocationArray
  ].flatMap(nodes => (nodes || []).map(node => normalizeDiagramModelKey(node && node.key)).filter(Boolean)));
}

function getDiagramModelNodes(ctrl) {
  return [
    ctrl.groupsArray,
    ctrl.nodeEquipmentArray,
    ctrl.nodePortArray,
    ctrl.nodeDirectDatasetArray,
    ctrl.nodeDatasetArray,
    ctrl.nodeLocationArray
  ].flatMap(nodes => nodes || []);
}

function getRenderedDiagramPortId(node, portId) {
  const normalized = normalizeDiagramModelKey(portId);
  if (!normalized) return null;
  if (node && node.category === 'Port') return normalized;
  const parts = normalized.split('_');
  return parts.length > 1 ? parts[1] : parts[0];
}

function addPortIdAlias(portIdMap, alias, renderedPortId) {
  const normalizedAlias = normalizeDiagramModelKey(alias);
  if (normalizedAlias && renderedPortId) portIdMap.set(normalizedAlias, renderedPortId);
}

function getNodePortIdMap(node) {
  const portIdMap = new Map();
  ['leftArray', 'rightArray', 'topArray', 'bottomArray', 'centerArray'].forEach(side => {
    (node && Array.isArray(node[side]) ? node[side] : []).forEach(port => {
      const storedPortId = normalizeDiagramModelKey(port && port.portId);
      const renderedPortId = getRenderedDiagramPortId(node, storedPortId);
      if (!storedPortId || !renderedPortId) return;

      addPortIdAlias(portIdMap, storedPortId, renderedPortId);
      addPortIdAlias(portIdMap, renderedPortId, renderedPortId);
      if (storedPortId.indexOf('Port_') !== 0) addPortIdAlias(portIdMap, `Port_${storedPortId}`, renderedPortId);
      if (storedPortId.indexOf('Controls_') !== 0) addPortIdAlias(portIdMap, `Controls_${storedPortId}`, renderedPortId);
    });
  });
  return portIdMap;
}

function getDiagramModelPortIdsByNodeKey(ctrl) {
  const portIdsByNodeKey = new Map();
  getDiagramModelNodes(ctrl).forEach(node => {
    const nodeKey = normalizeDiagramModelKey(node && node.key);
    if (!nodeKey) return;
    portIdsByNodeKey.set(nodeKey, getNodePortIdMap(node));
  });
  return portIdsByNodeKey;
}

function normalizeLinkPortIdForNode(portId, nodePortIdMap) {
  const normalized = normalizeDiagramModelKey(portId);
  if (!normalized || !nodePortIdMap || nodePortIdMap.size === 0) return '';
  if (nodePortIdMap.has(normalized)) return nodePortIdMap.get(normalized);

  const portCandidate = normalized.indexOf('Port_') === 0 ? normalized : `Port_${normalized}`;
  if (nodePortIdMap.has(portCandidate)) return nodePortIdMap.get(portCandidate);

  const controlsCandidate = normalized.indexOf('Controls_') === 0 ? normalized : `Controls_${normalized}`;
  if (nodePortIdMap.has(controlsCandidate)) return nodePortIdMap.get(controlsCandidate);

  const unprefixedCandidate = normalized.indexOf('_') >= 0 ? normalized.split('_')[1] : null;
  if (unprefixedCandidate && nodePortIdMap.has(unprefixedCandidate)) {
    return nodePortIdMap.get(unprefixedCandidate);
  }

  return '';
}

function filterLinksToExistingEndpoints(links, nodeKeys) {
  return (links || []).filter(link => {
    if (!link) return false;
    const fromKey = normalizeDiagramModelKey(link.from);
    const toKey = normalizeDiagramModelKey(link.to);
    return !!(fromKey && toKey && nodeKeys.has(fromKey) && nodeKeys.has(toKey));
  });
}

function serializeGoLinkPoints(points) {
  var serialized = [];
  if (!points) return serialized;

  const addPoint = point => {
    const parsed = parseDiagramPoint(point);
    if (parsed) serialized.push(parsed);
  };

  if (Array.isArray(points)) {
    points.forEach(addPoint);
    return serialized;
  }
  if (points.h && Array.isArray(points.h)) {
    points.h.forEach(addPoint);
    return serialized;
  }
  if (points.r && Array.isArray(points.r)) {
    points.r.forEach(addPoint);
    return serialized;
  }
  if (typeof points.toArray === 'function') {
    points.toArray().forEach(addPoint);
    return serialized;
  }

  if (typeof points.each === 'function') {
    points.each(addPoint);
  }
  return serialized;
}

function snapCoordinateToCellCenter(value, cellSize) {
  if (!Number.isFinite(value) || !Number.isFinite(cellSize) || cellSize <= 0) return value;
  return (Math.round((value - (cellSize / 2)) / cellSize) * cellSize) + (cellSize / 2);
}

function getNearbyCellCenter(value, cellSize, tolerance) {
  const snapped = snapCoordinateToCellCenter(value, cellSize);
  if (!Number.isFinite(snapped)) return value;
  return Math.abs(snapped - value) <= tolerance ? snapped : value;
}

function normalizeNearlyStraightLinkPoints(points, routeKind) {
  const normalizedInput = Array.isArray(points)
    ? points.map(parseDiagramPoint).filter(Boolean)
    : [];
  if (normalizedInput.length < 2) return [];

  const routeTolerance = 1;
  const snapTolerance = routeKind === 'externalFlag' ? Infinity : 1;
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  normalizedInput.forEach(point => {
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minY = Math.min(minY, point.y);
    maxY = Math.max(maxY, point.y);
  });

  if ((maxY - minY) <= routeTolerance) {
    const targetY = getNearbyCellCenter((minY + maxY) / 2, diagramConfiguration.calculateCellHeight(), snapTolerance);
    return normalizedInput.map(point => ({ x: point.x, y: targetY }));
  }

  if ((maxX - minX) <= routeTolerance) {
    const targetX = getNearbyCellCenter((minX + maxX) / 2, diagramConfiguration.calculateCellWidth(), snapTolerance);
    return normalizedInput.map(point => ({ x: targetX, y: point.y }));
  }

  return normalizedInput;
}

function normalizeStoredRoute(route) {
  const normalizedRoute = (Array.isArray(route) ? route : []).map(point => {
    if (!point || typeof point !== 'object') return null;
    const latRaw = point.lat !== undefined && point.lat !== null ? point.lat : point.latitude;
    const lngRaw = point.long !== undefined && point.long !== null ? point.long : point.lng;
    if (latRaw === undefined || latRaw === null || lngRaw === undefined || lngRaw === null) return null;
    const lat = Number(latRaw);
    const lng = Number(lngRaw);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, long: lng };
  }).filter(Boolean);

  return normalizedRoute.length >= 2 ? normalizedRoute : null;
}

function applyGoLinkPoints(link, points) {
  if (!link || !Array.isArray(points)) return;
  const goPoints = new go.List();
  if (points.length >= 2) {
    points.forEach(point => goPoints.add(new go.Point(point.x, point.y)));
  }
  link.points = goPoints;
}

function sanitizeLiveLinkPoints(link, routeKind) {
  if (!link || !link.points) return [];
  const originalPoints = serializeGoLinkPoints(link.points);
  const originalPointCount = Number(link.points.count) || originalPoints.length;
  if (originalPoints.length !== originalPointCount || (originalPointCount > 0 && originalPoints.length < 2)) {
    const sanitizedPoints = normalizeNearlyStraightLinkPoints(originalPoints, routeKind);
    applyGoLinkPoints(link, sanitizedPoints);
    return sanitizedPoints;
  }
  return originalPoints;
}

function isHorizontalSpot(spot) {
  return !!(spot && (spot.x === 0 || spot.x === 1) && spot.y === 0.5);
}

function isVerticalSpot(spot) {
  return !!(spot && (spot.y === 0 || spot.y === 1) && spot.x === 0.5);
}

function getPortConnectionAxis(spot, portPoint, adjacentPoint) {
  if (isHorizontalSpot(spot)) return 'horizontal';
  if (isVerticalSpot(spot)) return 'vertical';
  if (!portPoint || !adjacentPoint) return null;
  return Math.abs(adjacentPoint.x - portPoint.x) >= Math.abs(adjacentPoint.y - portPoint.y) ? 'horizontal' : 'vertical';
}

function isSafeGoPoint(point) {
  return !!(point && isSafeDiagramCoordinate(point.x) && isSafeDiagramCoordinate(point.y));
}

function getPortConnectionPoint(port, spot) {
  if (!port || typeof port.getDocumentPoint !== 'function') return null;
  const point = isHorizontalSpot(spot) || isVerticalSpot(spot)
    ? port.getDocumentPoint(spot)
    : port.getDocumentPoint(go.Spot.Center);
  return isSafeGoPoint(point) ? point : null;
}

function alignFeedLinkEndToPort(link, isFrom) {
  if (!link || !link.data || link.data.class !== 'Feeds') return;
  if (!link.points || link.points.count < 2) return;

  const port = isFrom ? link.fromPort : link.toPort;
  if (!port) return;

  const spot = isFrom ? port.fromSpot : port.toSpot;
  const endpointIndex = isFrom ? 0 : link.points.count - 1;
  const adjacentIndex = isFrom ? 1 : link.points.count - 2;
  const originalEndpoint = link.points.get(endpointIndex).copy();
  const adjacentPoint = link.points.get(adjacentIndex).copy();
  if (!isSafeGoPoint(originalEndpoint) || !isSafeGoPoint(adjacentPoint)) return;
  const portPoint = getPortConnectionPoint(port, spot);
  const axis = getPortConnectionAxis(spot, portPoint, adjacentPoint);
  if (!portPoint || !axis) return;

  const points = link.points.copy();
  const originalAxisValue = axis === 'horizontal' ? originalEndpoint.y : originalEndpoint.x;
  const targetAxisValue = axis === 'horizontal' ? portPoint.y : portPoint.x;
  const step = isFrom ? 1 : -1;
  const axisTolerance = 1.5;

  for (let index = endpointIndex; index >= 0 && index < points.count; index += step) {
    const point = points.get(index).copy();
    const pointAxisValue = axis === 'horizontal' ? point.y : point.x;
    if (index !== endpointIndex && Math.abs(pointAxisValue - originalAxisValue) > axisTolerance) break;

    if (index === endpointIndex) {
      points.set(index, portPoint);
    } else if (axis === 'horizontal') {
      point.y = targetAxisValue;
      points.set(index, point);
    } else {
      point.x = targetAxisValue;
      points.set(index, point);
    }
  }

  link.points = points;
}

function alignFeedLinkEndpointsToPorts(link) {
  alignFeedLinkEndToPort(link, true);
  alignFeedLinkEndToPort(link, false);
}

function normalizeLiveDiagramLinkPoints(link, routeKind) {
  if (!link || !link.points) return [];
  let originalPoints = sanitizeLiveLinkPoints(link, routeKind);
  if (originalPoints.length < 2) return originalPoints;

  alignFeedLinkEndpointsToPorts(link);
  originalPoints = serializeGoLinkPoints(link.points);
  const normalizedPoints = normalizeNearlyStraightLinkPoints(originalPoints, routeKind);
  const changed = normalizedPoints.length !== originalPoints.length ||
    normalizedPoints.some((point, index) => {
      return !originalPoints[index] || point.x !== originalPoints[index].x || point.y !== originalPoints[index].y;
    });

  if (changed) {
    applyGoLinkPoints(link, normalizedPoints);
  }

  return normalizedPoints;
}

function getRecordRid(recordOrRid) {
  if (recordOrRid === undefined || recordOrRid === null) return null;
  if (typeof recordOrRid === 'object') return recordOrRid['@rid'] || null;
  return recordOrRid;
}

function isSameRid(recordOrRid, rid) {
  return getRecordRid(recordOrRid) === rid;
}

function normalizePortToken(value) {
  if (value === undefined || value === null) return null;
  const token = String(value).trim();
  if (!token || token === 'undefined' || token === 'null') return null;
  return token;
}

function findMatchingPort(ports, portIdRaw) {
  const token = normalizePortToken(portIdRaw);
  if (!token || !ports) return null;
  const unprefixedToken = token.startsWith('Port_') ? token.slice(5) : token;

  return Object.values(ports).find(port => {
    if (!port) return false;
    return [port.id, port.name, port.portId]
      .map(normalizePortToken)
      .filter(Boolean)
      .some(candidate => candidate === token || candidate === unprefixedToken || `Port_${candidate}` === token);
  });
}

function normalizeConnectionPortId(portIdRaw, ports) {
  const token = normalizePortToken(portIdRaw);
  if (!token) return null;
  if (token.startsWith('Controls_')) return token;

  const matchingPort = findMatchingPort(ports, token);
  const matchingPortId = matchingPort && normalizePortToken(matchingPort.id);
  if (matchingPortId) return `Port_${matchingPortId}`;

  if (token.startsWith('Port_')) return token;
  if (/^\d+$/.test(token)) return `Port_${token}`;
  return null;
}

function connectionMatchesPort(port, portIdRaw) {
  return !!findMatchingPort({ port }, portIdRaw);
}

function getCurrentRecord(ctrl) {
  return typeof ctrl.equipmentRecord === 'function' ? ctrl.equipmentRecord() : ctrl.equipmentRecord;
}

function getCurrentRecordRid(ctrl) {
  const record = getCurrentRecord(ctrl);
  return record && record['@rid'];
}

function isDiagramMode(ctrl) {
  const record = getCurrentRecord(ctrl);
  return ctrl.isDiagram === true || (record && record['@class'] === 'Diagram');
}

function isEquipmentDatasetSelectionEnabled(ctrl) {
  return ctrl.enableEquipmentDatasetSelection === true || isDiagramMode(ctrl);
}

function isGridShellEquipmentData(data) {
  return !!(data && (
    data.category === 'GridEquipment' ||
    data.gridShell === true ||
    data.gridShellPrototype === true
  ));
}

function isRoomNodeData(data) {
  return !!(data && data.category === 'Room');
}

function isDatasetNodeData(data) {
  return !!(data && data.category === 'Dataset' && data.key);
}

function isExternalDiagramFlagNodeData(data) {
  return !!(data && data.isExternalDiagramFlag === true && data.key);
}

function isDefaultDatasetForLayout(node) {
  return !!(node && (
    node.isDefaulDataset === true ||
    node.isDefaultDataset === true ||
    node.name === 'Default'
  ));
}

function isEquipmentNodeDataForDatasetSelection(data) {
  return !!(data &&
    data.key &&
    data.class &&
    Array.isArray(data.datasetsRids) &&
    !['Dataset', 'Port', 'Room', 'Group'].includes(data.category)
  );
}

function isEquipmentPartDataForClipboard(data) {
  return !!(data &&
    data.class &&
    data.class !== 'Feeds' &&
    !['Dataset', 'Port', 'Group'].includes(data.category)
  );
}

function getEquipmentDatasetKeysForSelection(equipmentData, nodeDataArray) {
  if (!isEquipmentNodeDataForDatasetSelection(equipmentData) || !Array.isArray(nodeDataArray)) {
    return [];
  }

  const equipmentRid = getRecordRid(equipmentData.key);
  const datasetRids = new Set((equipmentData.datasetsRids || []).map(getRecordRid).filter(Boolean));

  return nodeDataArray
    .filter(isDatasetNodeData)
    .filter(datasetData => {
      const datasetKey = getRecordRid(datasetData.key);
      return getRecordRid(datasetData.parentRecordRid) === equipmentRid || datasetRids.has(datasetKey);
    })
    .map(datasetData => datasetData.key);
}

function getEquipmentExternalFlagKeysForSelection(equipmentData, nodeDataArray) {
  if (!equipmentData || !equipmentData.key || !Array.isArray(nodeDataArray)) {
    return [];
  }

  const equipmentRid = getRecordRid(equipmentData.key);
  return nodeDataArray
    .filter(isExternalDiagramFlagNodeData)
    .filter(flagData => {
      return getRecordRid(flagData.externalForEquipmentRid) === equipmentRid ||
        getRecordRid(flagData.parentRid) === equipmentRid;
    })
    .map(flagData => flagData.key);
}

function getEquipmentDatasetNodesForLayout(equipmentNode, datasetNodes) {
  if (!equipmentNode || !Array.isArray(datasetNodes)) return [];

  const equipmentRid = getRecordRid(equipmentNode.key);
  const datasetRids = new Set((equipmentNode.datasetsRids || []).map(getRecordRid).filter(Boolean));

  return datasetNodes.filter(datasetNode => {
    if (!isDatasetNodeData(datasetNode)) return false;
    const datasetKey = getRecordRid(datasetNode.key);
    return getRecordRid(datasetNode.parentRecordRid) === equipmentRid || datasetRids.has(datasetKey);
  });
}

function sortDatasetsForEquipmentLayout(datasetNodes) {
  return (datasetNodes || []).slice().sort((left, right) => {
    const leftDefault = isDefaultDatasetForLayout(left);
    const rightDefault = isDefaultDatasetForLayout(right);
    if (leftDefault !== rightDefault) return leftDefault ? -1 : 1;
    return String(left.name || left.key || '').localeCompare(String(right.name || right.key || ''));
  });
}

function getDiagramCellWidth() {
  return typeof diagramConfiguration.calculateCellWidth === 'function'
    ? diagramConfiguration.calculateCellWidth()
    : 10;
}

function getDiagramCellHeight() {
  return typeof diagramConfiguration.calculateCellHeight === 'function'
    ? diagramConfiguration.calculateCellHeight()
    : 13;
}

function getPortArrayLength(node, arrayName) {
  return Array.isArray(node && node[arrayName]) ? node[arrayName].length : 0;
}

function hasDiagramSvgAssets(node) {
  if (!node) return false;
  if (node.svg || node.svgConverted || node.svgBackground || node.svgBackgroundConverted ||
    node.svgForeground || node.svgForegroundConverted || node.mapSvg) {
    return true;
  }
  return (Array.isArray(node.staticSvgs) ? node.staticSvgs : []).some(item => {
    return item && (item.svg || item.svgConverted);
  });
}

function getGridCellCountForPortCount(portCount, fallback) {
  const count = Math.max(Number(portCount) || 0, 0);
  if (count === 0) return fallback;
  return Math.max(fallback, count * 2);
}

function applyDefaultGridShellSizeForPorts(node) {
  if (!node || node.gridShell !== true || hasDiagramSvgAssets(node)) return node;

  const requiredRows = getGridCellCountForPortCount(
    Math.max(getPortArrayLength(node, 'leftArray'), getPortArrayLength(node, 'rightArray')),
    gridShellPortLayout.defaultRows
  );
  const requiredCols = getGridCellCountForPortCount(
    Math.max(
      getPortArrayLength(node, 'topArray'),
      getPortArrayLength(node, 'bottomArray'),
      getPortArrayLength(node, 'centerArray')
    ),
    gridShellPortLayout.defaultCols
  );

  const currentCols = Math.max(Number(node.cols) || 0, gridShellPortLayout.defaultCols);
  const currentRows = Math.max(Number(node.rows) || 0, gridShellPortLayout.defaultRows);
  const nextCols = Math.max(currentCols, requiredCols);
  const nextRows = Math.max(currentRows, requiredRows);
  if (nextCols === currentCols && nextRows === currentRows) return node;

  node.cols = nextCols;
  node.rows = nextRows;
  node.width = Math.max(Number(node.width) || gridShellPortLayout.defaultWidth, nextCols * getDiagramCellWidth());
  node.height = Math.max(Number(node.height) || gridShellPortLayout.defaultHeight, nextRows * getDiagramCellHeight());
  return node;
}

function normalizeExternalFlagPortSide(side) {
  const normalized = String(side || '').toLowerCase();
  return ['left', 'right', 'top', 'bottom'].includes(normalized) ? normalized : 'bottom';
}

function estimateExternalFlagSize(flagNode) {
  const nameLength = String(flagNode && flagNode.name || '').length;
  return {
    width: Math.max(externalFlagLayout.minWidth, 92 + (nameLength * 8)),
    height: externalFlagLayout.height
  };
}

function calculateExternalFlagLaneCount(count) {
  if (!Number.isFinite(count) || count <= 0) return 0;
  return Math.min(
    externalFlagLayout.maxLanes,
    Math.max(1, Math.ceil(count / externalFlagLayout.maxPerLane))
  );
}

function calculateExternalFlagLaneCountForPortSpacing(count, itemSize, itemGap, cellSize) {
  if (!Number.isFinite(count) || count <= 0) return 0;
  const portStep = Math.max((Number(cellSize) || 0) * 2, 1);
  const requiredLanes = Math.max(1, Math.ceil(((Number(itemSize) || 0) + (Number(itemGap) || 0)) / portStep));
  return Math.min(count, Math.max(calculateExternalFlagLaneCount(count), requiredLanes));
}

function getExternalFlagLayoutPoint(node) {
  return parseDiagramPoint(node && node.loc) || parseDiagramPoint(node && node.latlong) || { x: 0, y: 0 };
}

function getExternalFlagEntrySlot(entry) {
  const slot = Number(entry && entry.portSlot !== undefined
    ? entry.portSlot
    : entry && entry.flagNode && entry.flagNode.externalPortSlot);
  return Number.isFinite(slot) ? slot : 0;
}

function getExternalFlagEntrySideCount(entry) {
  const count = Number(entry && entry.portSideCount !== undefined
    ? entry.portSideCount
    : entry && entry.flagNode && entry.flagNode.externalPortSideCount);
  return Number.isFinite(count) && count > 0 ? count : 0;
}

function getExternalFlagEntryKey(entry) {
  return String(entry && entry.flagNode && entry.flagNode.key || '');
}

function sortExternalFlagEntries(flagEntries) {
  return (flagEntries || []).slice().sort((left, right) => {
    const slotDiff = getExternalFlagEntrySlot(left) - getExternalFlagEntrySlot(right);
    if (slotDiff !== 0) return slotDiff;
    const leftName = String(left && left.flagNode && left.flagNode.name || getExternalFlagEntryKey(left));
    const rightName = String(right && right.flagNode && right.flagNode.name || getExternalFlagEntryKey(right));
    return leftName.localeCompare(rightName);
  });
}

function normalizeGridCellCount(value, fallback) {
  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed > 0) return Math.max(Math.round(parsed), 1);
  return Math.max(Math.round(fallback), 1);
}

function getGridCellCountFromPixelSize(value, cellSize, fallback) {
  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed > 0 && Number.isFinite(cellSize) && cellSize > 0) {
    return normalizeGridCellCount(parsed / cellSize, fallback);
  }
  return normalizeGridCellCount(undefined, fallback);
}

function getExternalFlagEquipmentGridSize(equipmentNode) {
  return {
    cols: normalizeGridCellCount(
      equipmentNode && equipmentNode.cols,
      getGridCellCountFromPixelSize(equipmentNode && equipmentNode.width, getDiagramCellWidth(), gridShellPortLayout.defaultCols)
    ),
    rows: normalizeGridCellCount(
      equipmentNode && equipmentNode.rows,
      getGridCellCountFromPixelSize(equipmentNode && equipmentNode.height, getDiagramCellHeight(), gridShellPortLayout.defaultRows)
    )
  };
}

function getPortCellIndexForExternalFlag(itemIndex, portCount, cellCount) {
  const count = Math.max(Number(portCount) || 0, 1);
  const gridCells = Math.max(Math.round(Number(cellCount) || 0), 1);
  const gap = count > 1 && (1 + ((count - 1) * 2)) <= gridCells ? 2 : 1;
  const occupiedCells = 1 + ((count - 1) * gap);
  const start = Math.max(Math.floor((gridCells - occupiedCells) / 2), 0);
  return Math.max(0, Math.min(gridCells - 1, start + (itemIndex * gap)));
}

function getExternalFlagPortAxisOffset(equipmentNode, entry, portSide) {
  const isSidePort = portSide === 'left' || portSide === 'right';
  const gridSize = getExternalFlagEquipmentGridSize(equipmentNode);
  const slot = Math.max(Math.round(getExternalFlagEntrySlot(entry)), 0);
  const sideCount = Math.max(Math.round(getExternalFlagEntrySideCount(entry)), slot + 1, 1);
  const cellCount = Math.max(
    isSidePort ? gridSize.rows : gridSize.cols,
    getGridCellCountForPortCount(sideCount, 0)
  );
  const cellSize = isSidePort ? getDiagramCellHeight() : getDiagramCellWidth();
  return (getPortCellIndexForExternalFlag(slot, sideCount, cellCount) + 0.5) * cellSize;
}

function clampExternalFlagAxisPosition(value, minimum, maximum) {
  if (!Number.isFinite(value) || !Number.isFinite(minimum) || !Number.isFinite(maximum) || maximum < minimum) {
    return value;
  }
  return Math.max(minimum, Math.min(maximum, value));
}

function calculateExternalFlagLayout(equipmentNode, flagEntries, portSide) {
  const entries = sortExternalFlagEntries(flagEntries)
    .filter(entry => entry && entry.flagNode && entry.flagNode.key);
  if (!equipmentNode || entries.length === 0) return {};

  const normalizedSide = normalizeExternalFlagPortSide(portSide);
  const equipmentLoc = getExternalFlagLayoutPoint(equipmentNode);
  const equipmentSize = estimateDiagramNodeSize(equipmentNode);
  const flagSizes = entries.map(entry => estimateExternalFlagSize(entry.flagNode));
  const maxFlagWidth = Math.max.apply(null, flagSizes.map(size => size.width));
  const maxFlagHeight = Math.max.apply(null, flagSizes.map(size => size.height));
  const layout = {};

  if (normalizedSide === 'left' || normalizedSide === 'right') {
    const laneCount = calculateExternalFlagLaneCountForPortSpacing(
      entries.length,
      maxFlagHeight,
      externalFlagLayout.verticalGap,
      getDiagramCellHeight()
    );

    entries.forEach((entry, index) => {
      const lane = index % laneCount;
      const portCenterY = equipmentLoc.y + getExternalFlagPortAxisOffset(equipmentNode, entry, normalizedSide);
      layout[entry.flagNode.key] = {
        x: normalizedSide === 'left'
          ? equipmentLoc.x - externalFlagLayout.gapFromEquipment - maxFlagWidth -
            (lane * (maxFlagWidth + externalFlagLayout.laneGap))
          : equipmentLoc.x + equipmentSize.width + externalFlagLayout.gapFromEquipment +
            (lane * (maxFlagWidth + externalFlagLayout.laneGap)),
        y: clampExternalFlagAxisPosition(
          portCenterY - (maxFlagHeight / 2),
          equipmentLoc.y,
          equipmentLoc.y + equipmentSize.height - maxFlagHeight
        )
      };
    });
    return layout;
  }

  const laneCount = calculateExternalFlagLaneCountForPortSpacing(
    entries.length,
    maxFlagWidth,
    externalFlagLayout.horizontalGap,
    getDiagramCellWidth()
  );

  entries.forEach((entry, index) => {
    const lane = index % laneCount;
    const portCenterX = equipmentLoc.x + getExternalFlagPortAxisOffset(equipmentNode, entry, normalizedSide);
    layout[entry.flagNode.key] = {
      x: clampExternalFlagAxisPosition(
        portCenterX - (maxFlagWidth / 2),
        equipmentLoc.x,
        equipmentLoc.x + equipmentSize.width - maxFlagWidth
      ),
      y: normalizedSide === 'top'
        ? equipmentLoc.y - externalFlagLayout.gapFromEquipment - maxFlagHeight -
          (lane * (maxFlagHeight + externalFlagLayout.laneGap))
        : equipmentLoc.y + equipmentSize.height + externalFlagLayout.gapFromEquipment +
          (lane * (maxFlagHeight + externalFlagLayout.laneGap))
    };
  });

  return layout;
}

function shouldIncludePortNodeInDefaultLayoutBounds(node) {
  return !isExternalDiagramFlagNodeData(node) || node.hasStoredExternalFlagLocation === true;
}

function getMoveDiagramUrl(ctrl) {
  const recordRid = getCurrentRecordRid(ctrl);
  if (isDiagramMode(ctrl)) return `/api/v1/diagram/${recordRid.slice(1)}/movediagram`;
  return `/api/v1/customequipment/${recordRid.slice(1)}/movediagram`;
}

function getChildEquipmentEdges(record, ctrl) {
  if (!record) return [];
  if (isDiagramMode(ctrl) || record['@class'] === 'Diagram') return record.out_hasDiagramEquipment || [];
  if (record['@class'] === 'System') return record.out_hasEquipment || [];
  return record.out_hasComponent || [];
}

function normalizeLinkPortId(portIdRaw) {
  const token = normalizePortToken(portIdRaw);
  if (!token) return '';
  return token.startsWith('Port_') ? token.slice(5) : token;
}

function getGoPortData(port) {
  if (port && port.data && typeof port.data === 'object') return port.data;
  if (port && port.panel && port.panel.data && typeof port.panel.data === 'object') return port.panel.data;
  return {};
}

function getGoPortId(port) {
  const directPortId = normalizePortToken(port && port.portId);
  if (directPortId) return directPortId;
  return normalizePortToken(getGoPortData(port).portId) || '';
}

function getGoPortServiceClass(port) {
  return serviceTaxonomy.getServiceClass(port) || serviceTaxonomy.getServiceClass(getGoPortData(port));
}

function getLinkCreatedServiceClass(link) {
  return getGoPortServiceClass(link && link.fromPort) || getGoPortServiceClass(link && link.toPort);
}

function isControlsGoPort(port) {
  return getGoPortServiceClass(port) === 'Controls' || getGoPortData(port).category === 'Controls';
}

function getGoLinkRouteKind(link) {
  const fromData = link && link.fromNode && link.fromNode.data;
  const toData = link && link.toNode && link.toNode.data;
  const linkData = link && link.data;
  const hasExternalFlagEndpoint =
    (fromData && fromData.isExternalDiagramFlag) ||
    (toData && toData.isExternalDiagramFlag) ||
    String(linkData && linkData.from || '').indexOf('ExternalPort_') === 0 ||
    String(linkData && linkData.to || '').indexOf('ExternalPort_') === 0;

  return hasExternalFlagEndpoint ? 'externalFlag' : 'included';
}

function applyStoredRoute(linkData, edge, diagramRid, useGeoPositioning, options) {
  if (!edge || !edge.diagramData || !edge.diagramData[diagramRid]) return;
  const diagramEntry = edge.diagramData[diagramRid];
  const routeKind = (options && options.routeKind) || diagramEntry.routeKind;
  if (options && options.excludeExternalFlagRoute && diagramEntry.externalFlagLoc && diagramEntry.routeKind !== 'included') {
    return;
  }
  if (options && options.excludeIncludedRoute && diagramEntry.routeKind === 'included') {
    return;
  }

  if (useGeoPositioning && diagramEntry.route) {
    const route = normalizeStoredRoute(diagramEntry.route);
    if (route) linkData.route = route;
  } else if (!useGeoPositioning && diagramEntry.points) {
    const points = normalizeNearlyStraightLinkPoints(diagramEntry.points, routeKind);
    if (points.length >= 2) linkData.points = points;
  }
}

function parseDiagramPoint(value) {
  if (Array.isArray(value) && value.length >= 2) {
    if (value[0] === undefined || value[0] === null || value[1] === undefined || value[1] === null) return null;
    const x = Number(value[0]);
    const y = Number(value[1]);
    if (isSafeDiagramCoordinate(x) && isSafeDiagramCoordinate(y)) return { x, y };
  }

  if (value && typeof value === 'object') {
    const xRaw = value.x !== undefined && value.x !== null ? value.x : value.S;
    const yRaw = value.y !== undefined && value.y !== null ? value.y : value.P;
    if (xRaw === undefined || xRaw === null || yRaw === undefined || yRaw === null) return null;
    const x = Number(xRaw);
    const y = Number(yRaw);
    if (isSafeDiagramCoordinate(x) && isSafeDiagramCoordinate(y)) return { x, y };
  }

  if (typeof value === 'string') {
    const parts = value.trim().split(/[\s,]+/).map(Number);
    if (parts.length >= 2 && isSafeDiagramCoordinate(parts[0]) && isSafeDiagramCoordinate(parts[1])) {
      return { x: parts[0], y: parts[1] };
    }
  }

  return null;
}

function formatDiagramPoint(point) {
  return `${point.x.toFixed(2)} ${point.y.toFixed(2)}`;
}

function sanitizeDiagramNodeLayout(node) {
  if (!node) return;
  const point = parseDiagramPoint(node.latlong) || parseDiagramPoint(node.loc);
  if (point) {
    node.latlong = [point.x, point.y];
  } else {
    node.latlong = [0, 0];
  }

  ['width', 'height'].forEach(key => {
    if (node[key] === undefined || node[key] === null || node[key] === '') return;
    const dimension = readSafePositiveDimension(node[key]);
    if (dimension === null) {
      delete node[key];
    } else {
      node[key] = dimension;
    }
  });

  ['cols', 'rows'].forEach(key => {
    if (node[key] === undefined || node[key] === null || node[key] === '') return;
    const count = readSafePositiveDimension(node[key], maxSafeGridCellCount);
    if (count === null) {
      delete node[key];
    } else {
      node[key] = Math.round(count);
    }
  });
}

function sanitizeDiagramModelCoordinates(ctrl) {
  [
    ctrl.groupsArray,
    ctrl.nodeEquipmentArray,
    ctrl.nodePortArray,
    ctrl.nodeDirectDatasetArray,
    ctrl.nodeDatasetArray,
    ctrl.nodeLocationArray
  ].forEach(nodes => {
    (nodes || []).forEach(sanitizeDiagramNodeLayout);
  });

  [ctrl.linkControlsDataArray, ctrl.linkFeedsDataArray, ctrl.linkContainsDataArray].forEach(links => {
    (links || []).forEach(link => {
      if (!link) return;
      if (Array.isArray(link.points)) {
        const points = normalizeNearlyStraightLinkPoints(link.points, link.routeKind);
        if (points.length >= 2) {
          link.points = points;
        } else {
          delete link.points;
        }
      }
      if (Array.isArray(link.route)) {
        const route = normalizeStoredRoute(link.route);
        if (route) {
          link.route = route;
        } else {
          delete link.route;
        }
      }
    });
  });

  const nodeKeys = getDiagramModelNodeKeySet(ctrl);
  const portIdsByNodeKey = getDiagramModelPortIdsByNodeKey(ctrl);
  ctrl.linkControlsDataArray = filterLinksToExistingEndpoints(ctrl.linkControlsDataArray, nodeKeys);
  ctrl.linkFeedsDataArray = filterLinksToExistingEndpoints(ctrl.linkFeedsDataArray, nodeKeys);
  ctrl.linkContainsDataArray = filterLinksToExistingEndpoints(ctrl.linkContainsDataArray, nodeKeys);

  [ctrl.linkControlsDataArray, ctrl.linkFeedsDataArray, ctrl.linkContainsDataArray].forEach(links => {
    (links || []).forEach(link => {
      const fromKey = normalizeDiagramModelKey(link && link.from);
      const toKey = normalizeDiagramModelKey(link && link.to);
      // GoJS receives template-specific rendered port IDs; persisted edge data keeps its original port IDs.
      link.fromGoPortId = normalizeLinkPortIdForNode(link && link.fromPortId, portIdsByNodeKey.get(fromKey));
      link.toGoPortId = normalizeLinkPortIdForNode(link && link.toPortId, portIdsByNodeKey.get(toKey));
    });
  });
}

function mergeDiagramDataEntry(diagramData, diagramRid, entry) {
  const currentDiagramData = diagramData && typeof diagramData === 'object'
    ? diagramData
    : {};
  const currentEntry = currentDiagramData[diagramRid] && typeof currentDiagramData[diagramRid] === 'object'
    ? currentDiagramData[diagramRid]
    : {};

  return Object.assign({}, currentDiagramData, {
    [diagramRid]: Object.assign({}, currentEntry, entry)
  });
}

function getStoredDiagramLayout(node, diagramRid) {
  if (!node || !node.diagramData || !diagramRid) return null;
  return node.diagramData[diagramRid] || null;
}

function hasStoredDiagramLocation(node, diagramRid) {
  const layout = getStoredDiagramLayout(node, diagramRid);
  return !!(layout && parseDiagramPoint(layout.loc));
}

function getNodeDiagramPoint(node, diagramRid) {
  const storedLayout = getStoredDiagramLayout(node, diagramRid);
  const storedPoint = storedLayout && parseDiagramPoint(storedLayout.loc);
  if (storedPoint) return storedPoint;

  return parseDiagramPoint(node && node.loc) || parseDiagramPoint(node && node.latlong);
}

function estimateDiagramNodeSize(node) {
  if (!node) return { width: 0, height: 0 };

  const width = Number(node.width);
  const height = Number(node.height);

  if (node.category === 'Dataset') {
    return {
      width: Number.isFinite(width) && width > 0 ? width : 280,
      height: Number.isFinite(height) && height > 0
        ? height
        : Math.max(96, 56 + ((node.columns || []).length * 22))
    };
  }

  if (node.category === 'Room') {
    return {
      width: Number.isFinite(width) && width > 0 ? width : 860,
      height: Number.isFinite(height) && height > 0 ? height : 260
    };
  }

  return {
    width: Number.isFinite(width) && width > 0 ? width : 180,
    height: Number.isFinite(height) && height > 0 ? height : 120
  };
}

function calculateNodeBounds(nodes, diagramRid) {
  return (nodes || []).reduce((bounds, node) => {
    const point = getNodeDiagramPoint(node, diagramRid);
    if (!point) return bounds;

    const size = estimateDiagramNodeSize(node);
    const nodeBounds = {
      minX: point.x,
      minY: point.y,
      maxX: point.x + size.width,
      maxY: point.y + size.height
    };

    if (!bounds) return nodeBounds;

    return {
      minX: Math.min(bounds.minX, nodeBounds.minX),
      minY: Math.min(bounds.minY, nodeBounds.minY),
      maxX: Math.max(bounds.maxX, nodeBounds.maxX),
      maxY: Math.max(bounds.maxY, nodeBounds.maxY)
    };
  }, null);
}

function setDefaultDiagramNodeLocation(node, diagramRid, point) {
  node.latlong = [point.x, point.y];
  node.loc = formatDiagramPoint(point);

  const diagramData = node.diagramData && typeof node.diagramData === 'object'
    ? Object.assign({}, node.diagramData)
    : {};
  const layout = diagramData[diagramRid] && typeof diagramData[diagramRid] === 'object'
    ? Object.assign({}, diagramData[diagramRid])
    : {};

  diagramData[diagramRid] = Object.assign({}, layout, { loc: node.loc });
  node.diagramData = diagramData;
}

function setDefaultDatasetLocation(node, diagramRid, point) {
  setDefaultDiagramNodeLocation(node, diagramRid, point);
}

function getDatasetTileColumnCount(datasetNodes) {
  const datasetCount = (datasetNodes || []).length;
  if (datasetCount === 0) return 0;
  return Math.min(3, Math.max(1, Math.ceil(Math.sqrt(datasetCount))));
}

function calculateDatasetTileGridSize(datasetNodes) {
  const datasets = datasetNodes || [];
  const columns = getDatasetTileColumnCount(datasets);
  if (columns === 0) return { width: 0, height: 0 };

  let gridHeight = 0;
  for (let index = 0; index < datasets.length; index += columns) {
    const row = datasets.slice(index, index + columns);
    const rowHeight = Math.max.apply(null, row.map(node => estimateDiagramNodeSize(node).height));
    if (index > 0) gridHeight += diagramAutoLayout.datasetTileGapY;
    gridHeight += rowHeight;
  }

  const columnCount = Math.min(columns, datasets.length);
  return {
    width: (columnCount * diagramAutoLayout.datasetTileWidth) +
      ((columnCount - 1) * diagramAutoLayout.datasetTileGapX),
    height: gridHeight
  };
}

function setDefaultDatasetTileLocations(datasetNodes, diagramRid, point) {
  const datasets = sortDatasetsForEquipmentLayout(datasetNodes);
  const columns = getDatasetTileColumnCount(datasets);
  if (columns === 0) return;

  let rowY = point.y;
  for (let index = 0; index < datasets.length; index += columns) {
    const row = datasets.slice(index, index + columns);
    const rowHeight = Math.max.apply(null, row.map(node => estimateDiagramNodeSize(node).height));

    row.forEach((node, colIndex) => {
      setDefaultDatasetLocation(node, diagramRid, {
        x: point.x + (colIndex * (diagramAutoLayout.datasetTileWidth + diagramAutoLayout.datasetTileGapX)),
        y: rowY
      });
    });

    rowY += rowHeight + diagramAutoLayout.datasetTileGapY;
  }
}

function calculateEquipmentDatasetClusterSize(equipmentNode, datasetNodes) {
  const datasets = datasetNodes || [];
  const equipmentSize = estimateDiagramNodeSize(equipmentNode);
  const datasetGridSize = calculateDatasetTileGridSize(datasets);
  return {
    width: Math.max(diagramAutoLayout.minimumClusterWidth, equipmentSize.width, datasetGridSize.width),
    height: equipmentSize.height + (datasets.length > 0
      ? diagramAutoLayout.equipmentDatasetGapY + datasetGridSize.height
      : 0)
  };
}

function setDefaultEquipmentDatasetClusterLocations(equipmentNode, datasetNodes, diagramRid, point) {
  setDefaultDiagramNodeLocation(equipmentNode, diagramRid, point);
  if (!datasetNodes || datasetNodes.length === 0) return;

  const equipmentSize = estimateDiagramNodeSize(equipmentNode);
  setDefaultDatasetTileLocations(datasetNodes, diagramRid, {
    x: point.x,
    y: point.y + equipmentSize.height + diagramAutoLayout.equipmentDatasetGapY
  });
}

function getBottomRightLayoutStart(bounds) {
  return {
    x: bounds ? bounds.maxX + diagramAutoLayout.bottomRightGapX : 0,
    y: bounds ? bounds.maxY + diagramAutoLayout.bottomRightGapY : 0
  };
}

function setDefaultEquipmentDatasetClusters(clusters, diagramRid, bounds) {
  if (!Array.isArray(clusters) || clusters.length === 0) return;

  const columns = Math.min(3, Math.max(1, Math.ceil(Math.sqrt(clusters.length))));
  const start = getBottomRightLayoutStart(bounds);
  let rowY = start.y;

  for (let index = 0; index < clusters.length; index += columns) {
    const row = clusters.slice(index, index + columns);
    let rowX = start.x;
    let rowHeight = 0;

    row.forEach(cluster => {
      const clusterSize = calculateEquipmentDatasetClusterSize(cluster.equipmentNode, cluster.datasetNodes);
      setDefaultEquipmentDatasetClusterLocations(cluster.equipmentNode, cluster.datasetNodes, diagramRid, {
        x: rowX,
        y: rowY
      });
      rowX += clusterSize.width + diagramAutoLayout.clusterGapX;
      rowHeight = Math.max(rowHeight, clusterSize.height);
    });

    rowY += rowHeight + diagramAutoLayout.clusterGapY;
  }
}

function assignDefaultDiagramEquipmentLocations(ctrl) {
  if (!ctrl || ctrl.useGeoPositioning || !isDiagramMode(ctrl)) return;

  const diagramRecord = ctrl.equipmentRecord && ctrl.equipmentRecord();
  const diagramRid = diagramRecord && diagramRecord['@rid'];
  if (!diagramRid) return;

  const equipmentNodes = (ctrl.nodeEquipmentArray || [])
    .filter(node => node && node.category === 'Equipment');
  const unpositionedEquipment = equipmentNodes
    .filter(node => !hasStoredDiagramLocation(node, diagramRid));

  if (unpositionedEquipment.length === 0) return;

  const datasetNodes = [
    ...(ctrl.nodeDirectDatasetArray || []),
    ...(ctrl.nodeDatasetArray || [])
  ].filter(node => node && node.category === 'Dataset');
  const clusteredDatasetNodes = new Set();
  const clusters = unpositionedEquipment.map(equipmentNode => {
    const datasetNodesForEquipment = sortDatasetsForEquipmentLayout(
      getEquipmentDatasetNodesForLayout(equipmentNode, datasetNodes)
        .filter(datasetNode => !hasStoredDiagramLocation(datasetNode, diagramRid))
    );
    datasetNodesForEquipment.forEach(datasetNode => clusteredDatasetNodes.add(datasetNode));
    return {
      equipmentNode,
      datasetNodes: datasetNodesForEquipment
    };
  });

  const positionedNodes = [
    ...(ctrl.groupsArray || []),
    ...equipmentNodes.filter(node => hasStoredDiagramLocation(node, diagramRid)),
    ...(ctrl.nodePortArray || []).filter(shouldIncludePortNodeInDefaultLayoutBounds),
    ...(ctrl.nodeLocationArray || []),
    ...datasetNodes.filter(node => !clusteredDatasetNodes.has(node) && hasStoredDiagramLocation(node, diagramRid))
  ];
  const bounds = calculateNodeBounds(positionedNodes, diagramRid);

  setDefaultEquipmentDatasetClusters(clusters, diagramRid, bounds);
}

function assignDefaultDatasetTileLocations(ctrl) {
  if (!ctrl || ctrl.useGeoPositioning) return;

  const diagramRid = ctrl.equipmentRecord && ctrl.equipmentRecord() && ctrl.equipmentRecord()['@rid'];
  if (!diagramRid) return;

  const datasetNodes = [
    ...(ctrl.nodeDirectDatasetArray || []),
    ...(ctrl.nodeDatasetArray || [])
  ].filter(node => node && node.category === 'Dataset');
  const unpositionedDatasets = datasetNodes.filter(node => !hasStoredDiagramLocation(node, diagramRid));

  if (unpositionedDatasets.length === 0) return;

  if (isDiagramMode(ctrl)) {
    const equipmentNodes = (ctrl.nodeEquipmentArray || []).filter(node => node && node.key);
    const handledDatasets = new Set();

    equipmentNodes.forEach(equipmentNode => {
      const equipmentPoint = getNodeDiagramPoint(equipmentNode, diagramRid);
      if (!equipmentPoint) return;

      const datasetsForEquipment = sortDatasetsForEquipmentLayout(
        getEquipmentDatasetNodesForLayout(equipmentNode, unpositionedDatasets)
          .filter(datasetNode => !handledDatasets.has(datasetNode))
      );
      if (datasetsForEquipment.length === 0) return;

      const equipmentSize = estimateDiagramNodeSize(equipmentNode);
      setDefaultDatasetTileLocations(datasetsForEquipment, diagramRid, {
        x: equipmentPoint.x,
        y: equipmentPoint.y + equipmentSize.height + diagramAutoLayout.equipmentDatasetGapY
      });
      datasetsForEquipment.forEach(datasetNode => handledDatasets.add(datasetNode));
    });

    const remainingDatasets = unpositionedDatasets.filter(datasetNode => !handledDatasets.has(datasetNode));
    if (remainingDatasets.length === 0) return;
    unpositionedDatasets.splice(0, unpositionedDatasets.length, ...remainingDatasets);
  }

  const positionedNodes = [
    ...(ctrl.groupsArray || []),
    ...(ctrl.nodeEquipmentArray || []),
    ...(ctrl.nodePortArray || []).filter(shouldIncludePortNodeInDefaultLayoutBounds),
    ...(ctrl.nodeLocationArray || []),
    ...datasetNodes.filter(node => hasStoredDiagramLocation(node, diagramRid))
  ];
  const bounds = calculateNodeBounds(positionedNodes, diagramRid);

  const columns = Math.min(3, Math.max(1, Math.ceil(Math.sqrt(unpositionedDatasets.length))));
  const start = isDiagramMode(ctrl)
    ? getBottomRightLayoutStart(bounds)
    : {
        x: bounds ? bounds.maxX + diagramAutoLayout.bottomRightGapX : 0,
        y: bounds ? bounds.minY : 0
      };
  let rowY = start.y;

  for (let index = 0; index < unpositionedDatasets.length; index += columns) {
    const row = unpositionedDatasets.slice(index, index + columns);
    const rowHeight = Math.max.apply(null, row.map(node => estimateDiagramNodeSize(node).height));

    row.forEach((node, colIndex) => {
      setDefaultDatasetLocation(node, diagramRid, {
        x: start.x + (colIndex * (diagramAutoLayout.datasetTileWidth + diagramAutoLayout.datasetTileGapX)),
        y: rowY
      });
    });

    rowY += rowHeight + diagramAutoLayout.datasetTileGapY;
  }
}

function setDefaultExternalFlagLocation(flagNode, point) {
  flagNode.latlong = [point.x, point.y];
  flagNode.loc = formatDiagramPoint(point);
}

function assignDefaultExternalFlagLocations(ctrl) {
  if (!ctrl || ctrl.useGeoPositioning || !isDiagramMode(ctrl)) return;

  const equipmentByRid = new Map();
  (ctrl.nodeEquipmentArray || []).forEach(equipmentNode => {
    const equipmentRid = getRecordRid(equipmentNode && equipmentNode.key);
    if (equipmentRid) equipmentByRid.set(equipmentRid, equipmentNode);
  });
  if (equipmentByRid.size === 0) return;

  const groups = new Map();
  (ctrl.nodePortArray || [])
    .filter(isExternalDiagramFlagNodeData)
    .filter(flagNode => flagNode.hasStoredExternalFlagLocation !== true)
    .forEach(flagNode => {
      const equipmentRid = getRecordRid(flagNode.externalForEquipmentRid || flagNode.parentRid);
      const equipmentNode = equipmentByRid.get(equipmentRid);
      if (!equipmentNode) return;

      const portSide = normalizeExternalFlagPortSide(flagNode.externalPortSide || flagNode.direction);
      const groupKey = `${equipmentRid}:${portSide}`;
      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          equipmentNode,
          portSide,
          flags: []
        });
      }
      groups.get(groupKey).flags.push({
        flagNode,
        portSlot: flagNode.externalPortSlot,
        portSideCount: flagNode.externalPortSideCount
      });
    });

  groups.forEach(group => {
    const layout = calculateExternalFlagLayout(group.equipmentNode, group.flags, group.portSide);
    group.flags.forEach(entry => {
      const point = layout[entry.flagNode.key];
      if (point) setDefaultExternalFlagLocation(entry.flagNode, point);
    });
  });
}

function shouldIncludeDirectDatasetInDiagram(containsEdge) {
  const dataset = containsEdge && containsEdge.in;
  if (!dataset) return false;
  if (dataset.visible && dataset.visible == 'false') return false;
  if (dataset.hideInDiagram && dataset.hideInDiagram == 'true') return false;
  return true;
}

function shouldIncludeParentDatasetInDiagram(containsEdge) {
  const dataset = containsEdge && containsEdge.in;
  if (!dataset) return false;
  const diagramRid = arguments.length > 1 ? arguments[1] : null;
  const diagramVisibility = getDatasetDiagramVisibility(dataset, diagramRid);
  if (!isDefaultDatasetForDiagram(dataset) && dataset.type != undefined && dataset.type != 'list') return false;
  if (diagramVisibility !== null) return diagramVisibility;
  if (dataset.hideInParentDiagram == 'true') return false;
  return true;
}

function isDefaultDatasetForDiagram(dataset) {
  return !!(dataset && (dataset.name === 'Default' || dataset.isDefaultDataset === true || dataset.isDefaulDataset === true));
}

function getDatasetDiagramVisibility(dataset, diagramRid) {
  if (!dataset || !diagramRid || !dataset.diagramData || typeof dataset.diagramData !== 'object') {
    return null;
  }
  const entry = dataset.diagramData[diagramRid];
  if (!entry || typeof entry !== 'object' || entry.visible === undefined || entry.visible === null) {
    return null;
  }
  return parseDiagramVisibilityValue(entry.visible);
}

function parseDiagramVisibilityValue(value) {
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  if (typeof value === 'string') {
    const normalized = value.toLowerCase().trim();
    if (normalized === 'true' || normalized === '1' || normalized === 'yes') return true;
    if (normalized === 'false' || normalized === '0' || normalized === 'no') return false;
  }
  return Boolean(value);
}

function buildFeedsLinkData(edge, ctrl, from, to, fromPortId, toPortId, options) {
  const linkFeedsData = {
    class: edge['@class'],
    category: serviceTaxonomy.getServiceClass(edge),
    key: edge['@rid'],
    from: from,
    to: to,
    fromPortId: normalizeLinkPortId(fromPortId),
    toPortId: normalizeLinkPortId(toPortId),
    diagramData: edge.diagramData,
    linkColor: diagramConfiguration.findPortColor(serviceTaxonomy.getServiceClass(edge)),
    routing: ctrl.useGeoPositioning ? go.Link.Normal : go.Link.Orthogonal,
    minValue: edge.minValue,
    maxValue: edge.maxValue,
    pointsLinked: edge.pointsLinked || [],
    equipmentDiagramRid: ctrl.equipmentRecord()['@rid'],
    formula: edge.formula
  };
  if (options && options.routeKind) linkFeedsData.routeKind = options.routeKind;
  const linkDashPattern = diagramConfiguration.findLinkDashArray(serviceTaxonomy.getServiceClass(edge));
  linkFeedsData.dashPattern = linkDashPattern || [5, 5];
  applyStoredRoute(linkFeedsData, edge, ctrl.equipmentRecord()['@rid'], ctrl.useGeoPositioning, options);
  return linkFeedsData;
}

function addDiagramFeedLinks(ctrl, childEquipment) {
  const includedEquipmentRids = new Set(ctrl.nodeEquipmentArray.map(node => node.key));
  const includedEquipmentByRid = new Map();
  const addedFeedRids = new Set();
  const allowExternalConnectionFlags = isDiagramMode(ctrl);

  (childEquipment || []).forEach(edge => {
    if (edge && edge.in && edge.in['@rid']) {
      includedEquipmentByRid.set(edge.in['@rid'], edge.in);
    }
  });

  function feedUsesDefinedPortOnIncludedEndpoint(feedEdge, includedRid, side) {
    const includedRecord = includedEquipmentByRid.get(includedRid);
    if (!includedRecord) return false;
    const portId = side === 'from' ? feedEdge.fromPortId : feedEdge.toPortId;
    return !!normalizeConnectionPortId(portId, includedRecord.ports);
  }

  function hasIncludedComponentParent(record) {
    return (record && record.in_hasComponent || []).some(edge => {
      return includedEquipmentRids.has(getRecordRid(edge && edge.out));
    });
  }

  function normalizePortSide(position) {
    const normalized = String(position || '').toLowerCase();
    return ['left', 'right', 'top', 'bottom'].includes(normalized) ? normalized : 'bottom';
  }

  function getPortSideIndex(ports, targetPort, side) {
    const sidePorts = Object.values(ports || {}).filter(port => {
      return normalizePortSide((port && port.position) || (port && port.direction)) === side;
    });
    const index = sidePorts.findIndex(port => String(port && port.id) === String(targetPort && targetPort.id));
    return index >= 0 ? index : sidePorts.length;
  }

  function getPortSideCount(ports, side) {
    return Object.values(ports || {}).filter(port => {
      return normalizePortSide((port && port.position) || (port && port.direction)) === side;
    }).length;
  }

  function oppositeSide(side) {
    if (side === 'left') return 'right';
    if (side === 'right') return 'left';
    if (side === 'top') return 'bottom';
    return 'top';
  }

  function parseNodeLoc(node) {
    if (node && node.loc) {
      const parts = String(node.loc).split(' ').map(parseFloat);
      if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1])) return { x: parts[0], y: parts[1] };
    }
    if (node && Array.isArray(node.latlong) && node.latlong.length >= 2) {
      const x = parseFloat(node.latlong[0]);
      const y = parseFloat(node.latlong[1]);
      if (!isNaN(x) && !isNaN(y)) return { x, y };
    }
    return { x: 0, y: 0 };
  }

  function estimateEquipmentSize(node) {
    return {
      width: Number(node && node.width) || (diagramConfiguration.calculateCellWidth() * 7),
      height: Number(node && node.height) || (diagramConfiguration.calculateCellHeight() * 5)
    };
  }

  function getStoredExternalFlagPoint(feedEdge, diagramRid, side) {
    const diagramData = feedEdge && feedEdge.diagramData && feedEdge.diagramData[diagramRid];
    if (diagramData && diagramData.routeKind === 'included') return null;
    const storedFlagPoint = diagramData && parseDiagramPoint(diagramData.externalFlagLoc);
    if (storedFlagPoint) return storedFlagPoint;

    const points = diagramData && Array.isArray(diagramData.points) ? diagramData.points : null;
    if (!points || points.length === 0) return null;
    const point = side === 'outgoing' ? points[points.length - 1] : points[0];
    return parseDiagramPoint(point);
  }

  function defaultExternalFlagLocation(equipmentNode, portSide, portSlot, portSideCount) {
    const loc = parseNodeLoc(equipmentNode);
    const size = estimateEquipmentSize(equipmentNode);
    const cellWidth = diagramConfiguration.calculateCellWidth();
    const cellHeight = diagramConfiguration.calculateCellHeight();
    const flagGap = Math.max(cellWidth * 6, 56);
    const flagWidth = Math.max(cellWidth * 5, 48);
    const flagHeight = Math.max(cellHeight * 3, 36);
    const count = Math.max(Number(portSideCount) || 0, 1);
    const slot = Math.max(Number(portSlot) || 0, 0);
    const sideFraction = (slot + 1) / (count + 1);

    if (portSide === 'left') {
      return { x: loc.x - flagGap - flagWidth, y: loc.y + (size.height * sideFraction) - (flagHeight / 2) };
    }
    if (portSide === 'right') {
      return { x: loc.x + size.width + flagGap, y: loc.y + (size.height * sideFraction) - (flagHeight / 2) };
    }
    if (portSide === 'top') {
      return { x: loc.x + (size.width * sideFraction) - (flagWidth / 2), y: loc.y - flagGap - flagHeight };
    }
    return { x: loc.x + (size.width * sideFraction) - (flagWidth / 2), y: loc.y + size.height + flagGap };
  }

  function makeExternalFlagKey(feedEdge, includedRid, portId) {
    return [
      'ExternalPort',
      normalizeLinkPortId(feedEdge && feedEdge['@rid']),
      normalizeLinkPortId(includedRid),
      normalizeLinkPortId(portId)
    ].join('_').replace(/[^A-Za-z0-9_]/g, '_');
  }

  function addExternalConnectionFlag(includedRid, feedEdge, remoteRecord, side) {
    const node = ctrl.nodeEquipmentArray.find(equipmentNode => equipmentNode.key === includedRid);
    const includedRecord = includedEquipmentByRid.get(includedRid);
    if (!node || !includedRecord) return;

    const portId = side === 'outgoing' ? feedEdge.fromPortId : feedEdge.toPortId;
    const port = findMatchingPort(includedRecord.ports, portId);
    if (!port) return;

    const remoteName = remoteRecord && (remoteRecord.name || remoteRecord['@class']) || 'External';
    const portName = port.name || port.id || normalizeLinkPortId(portId);
    const portSide = normalizePortSide(port.position || port.direction);
    const portSlot = getPortSideIndex(includedRecord.ports, port, portSide);
    const portSideCount = getPortSideCount(includedRecord.ports, portSide);
    const includedPortId = normalizeConnectionPortId(portId, includedRecord.ports);
    const flagPortId = `External_${normalizeLinkPortId(feedEdge['@rid'])}_${normalizeLinkPortId(port.id)}`.replace(/[^A-Za-z0-9_]/g, '_');
    const flagKey = makeExternalFlagKey(feedEdge, includedRid, port.id);
    const storedPoint = getStoredExternalFlagPoint(feedEdge, ctrl.equipmentRecord()['@rid'], side);
    const flagLoc = storedPoint || defaultExternalFlagLocation(node, portSide, portSlot, portSideCount);
    const flagSide = oppositeSide(portSide);
    const flagColor = diagramConfiguration.findPortColor(serviceTaxonomy.getServiceClass(feedEdge));
    const flagPort = {
      portColor: flagColor,
      portName: portName,
      portId: flagPortId,
      type: side === 'outgoing' ? 'input' : 'output',
      category: serviceTaxonomy.getServiceClass(feedEdge)
    };
    const flagNode = {
      category: 'Port',
      key: flagKey,
      name: remoteName,
      type: flagPort.type,
      color: flagColor,
      parentRecordPorts: {},
      parentRid: includedRid,
      direction: portSide,
      directionAndType: [portSide, flagPort.type],
      class: serviceTaxonomy.getServiceClass(feedEdge),
      serviceClass: serviceTaxonomy.getServiceClass(feedEdge),
      isExternalDiagramFlag: true,
      externalFeedRid: feedEdge['@rid'],
      externalForEquipmentRid: includedRid,
      externalFlagSide: side,
      externalPortSide: portSide,
      externalPortSlot: portSlot,
      externalPortSideCount: portSideCount,
      hasStoredExternalFlagLocation: !!storedPoint,
      loc: formatDiagramPoint(flagLoc),
      latlong: [flagLoc.x, flagLoc.y],
      linkedEquipments: [{
        rid: getRecordRid(remoteRecord),
        class: remoteRecord && remoteRecord['@class'],
        name: remoteName
      }].filter(link => link.rid)
    };

    if (flagSide === 'left') flagNode.leftArray = [flagPort];
    if (flagSide === 'right') flagNode.rightArray = [flagPort];
    if (flagSide === 'top') flagNode.topArray = [flagPort];
    if (flagSide === 'bottom') flagNode.bottomArray = [flagPort];

    if (!ctrl.nodePortArray.find(portNode => portNode.key === flagKey)) {
      ctrl.nodePortArray.push(flagNode);
    }

    ctrl.linkFeedsDataArray.push(buildFeedsLinkData(
      feedEdge,
      ctrl,
      side === 'outgoing' ? includedRid : flagKey,
      side === 'outgoing' ? flagKey : includedRid,
      side === 'outgoing' ? includedPortId : flagPortId,
      side === 'outgoing' ? flagPortId : includedPortId,
      { excludeIncludedRoute: true, routeKind: 'externalFlag' }
    ));
  }

  function maybeAddFeed(feedEdge) {
    if (!feedEdge || !feedEdge['@rid'] || addedFeedRids.has(feedEdge['@rid'])) return;
    const fromRid = getRecordRid(feedEdge.out);
    const toRid = getRecordRid(feedEdge.in);
    const fromIncluded = includedEquipmentRids.has(fromRid);
    const toIncluded = includedEquipmentRids.has(toRid);
    if (!fromIncluded && !toIncluded) return;

    if (fromIncluded && toIncluded) {
      if (!feedUsesDefinedPortOnIncludedEndpoint(feedEdge, fromRid, 'from')) return;
      if (!feedUsesDefinedPortOnIncludedEndpoint(feedEdge, toRid, 'to')) return;
      ctrl.linkFeedsDataArray.push(buildFeedsLinkData(
        feedEdge,
        ctrl,
        fromRid,
        toRid,
        feedEdge.fromPortId,
        feedEdge.toPortId,
        { excludeExternalFlagRoute: true, routeKind: 'included' }
      ));
      addedFeedRids.add(feedEdge['@rid']);
      return;
    }

    if (fromIncluded) {
      if (!allowExternalConnectionFlags) return;
      if (!feedUsesDefinedPortOnIncludedEndpoint(feedEdge, fromRid, 'from')) return;
      if (hasIncludedComponentParent(feedEdge.in)) return;
      addExternalConnectionFlag(fromRid, feedEdge, feedEdge.in, 'outgoing');
      addedFeedRids.add(feedEdge['@rid']);
      return;
    }

    if (toIncluded) {
      if (!allowExternalConnectionFlags) return;
      if (!feedUsesDefinedPortOnIncludedEndpoint(feedEdge, toRid, 'to')) return;
      if (hasIncludedComponentParent(feedEdge.out)) return;
      addExternalConnectionFlag(toRid, feedEdge, feedEdge.out, 'incoming');
      addedFeedRids.add(feedEdge['@rid']);
      return;
    }
  }

  (childEquipment || []).forEach(edge => {
    const equipmentRecord = edge && edge.in;
    if (!equipmentRecord) return;
    (equipmentRecord.out_Feeds || []).forEach(feedEdge => maybeAddFeed(feedEdge));
    (equipmentRecord.in_Feeds || []).forEach(feedEdge => maybeAddFeed(feedEdge));
  });
}

go.Diagram.licenseKey = process.env.GOJS_LICENSE_KEY;


equipmentDiagram.oninit = function (vnode) {
  var ctrl = this;
  ctrl.forceReadOnly = vnode.attrs.forceReadOnly === true;
  ctrl.canvasId = vnode.attrs.canvasId || 'canvas';
  ctrl.groupingButtonId = vnode.attrs.groupingButtonId || 'groupingButton';
  ctrl.isEditingEnabled = function () {
    return !ctrl.forceReadOnly && edit.vm.editing();
  };
  ctrl.redrawDiagram = vnode.attrs.redrawDiagram();
  ctrl.selectedEquipmentRid = vnode.attrs.selectedEquipmentRid;
  ctrl.parentReinitialize = vnode.attrs.parentReinitialize;
  ctrl.parentReinitializeAndRedraw = vnode.attrs.parentReinitializeAndRedraw;
  ctrl.parentCtrl = vnode.attrs.parentCtrl;
  ctrl.isSystem = vnode.attrs.isSystem;
  ctrl.enableEquipmentDatasetSelection = vnode.attrs.enableEquipmentDatasetSelection === true;
  ctrl.isDiagram = vnode.attrs.isDiagram === true ||
    (typeof vnode.attrs.equipmentRecord === 'function' &&
      vnode.attrs.equipmentRecord() &&
      vnode.attrs.equipmentRecord()['@class'] === 'Diagram');

  ctrl.editing = prop();
  ctrl.editing(ctrl.isEditingEnabled());
  ctrl.useGeoPositioning = vnode.attrs.useGeoPositioning || false;
  ctrl.map = vnode.attrs.parentCtrl?.map;

  if (ctrl.useGeoPositioning) {
    ctrl.defaultZoom = ctrl.map?.getZoom();
  }

  ctrl.submitCallback = function () {
    equipmentModel.requestClassTree()
      .then(r => {
        vnode.attrs.parentReinitializeAndRedraw(true);
      })
  }
}

equipmentDiagram.oncreate = function (vnode) {
  var ctrl = this;
  ctrl.pointSubscriberObj = new pointSubscriber();
  ctrl.lastCopiedSelection = [];
  ctrl.lastEquipmentDatasetSelectionKey = null;
  ctrl.init = function () {
    ctrl.equipmentRecord = vnode.attrs.equipmentRecord;
    ctrl.equipmentRecordRid = ctrl.equipmentRecord()['@rid'];

    ctrl.nodeDirectDatasetArray = [];
    ctrl.nodePortArray = [];
    ctrl.nodeDatasetArray = [];
    ctrl.nodeLocationArray = [];
    ctrl.linkContainsDataArray = [];
    ctrl.linkFeedsDataArray = [];
    ctrl.linkControlsDataArray = [];

    let childEquipment = getChildEquipmentEdges(ctrl.equipmentRecord(), ctrl);
    ctrl.nodeEquipmentArray = childEquipment
      .filter(edge => {
        return ctrl.isEditingEnabled() || edge.in.hideInDiagram !== 'true';
      })
      .map(hasComponentEdge => {
        const staticSvgs = Array.isArray(hasComponentEdge.in.staticSvgs)
          ? hasComponentEdge.in.staticSvgs
              .slice()
              .sort((a, b) => (Number(a && a.key) || 0) - (Number(b && b.key) || 0))
              .map((item, index) => Object.assign({}, item, {
                key: index + 1,
                label: typeof item?.label === 'string' ? item.label : '',
                svg: typeof item?.svg === 'string' ? item.svg : '',
                svgConverted: typeof item?.svgConverted === 'string' ? item.svgConverted : '',
              }))
          : [];

        let node = {
          category: 'Equipment',
          class: hasComponentEdge.in['@class'],
          key: hasComponentEdge.in['@rid'],
          name: hasComponentEdge.in['name'],
          linkKey: hasComponentEdge['@rid'],
          linkCategory: hasComponentEdge['@class'],
          diagramData: hasComponentEdge.in.diagramData,
          leftArray: [],
          rightArray: [],
          topArray: [],
          bottomArray: [],
          centerArray: [],
          datasetsRids: (hasComponentEdge.in.out_Contains || []).map(c => c.in['@rid']),
          data: { ...hasComponentEdge.in.data },
          mapPolygonColor: hasComponentEdge.in.mapPolygonColor,
          mapPolygonOpacity: hasComponentEdge.in.mapPolygonOpacity,
          mapPolygonWeight: hasComponentEdge.in.mapPolygonWeight,
          mapPolygonFillColor: hasComponentEdge.in.mapPolygonFillColor,
          mapPolygonFillOpacity: hasComponentEdge.in.mapPolygonFillOpacity,
          mapSvg: hasComponentEdge.in.mapSvg,
          latitude: hasComponentEdge.in.latitude,
          longitude: hasComponentEdge.in.longitude,
          geojson: hasComponentEdge.in.geojson,
          animationType: hasComponentEdge.in.animationType,
          minValue: hasComponentEdge.in.minValue,
          maxValue: hasComponentEdge.in.maxValue,
          direction: hasComponentEdge.in.direction,
          hideInDiagram: hasComponentEdge.in.hideInDiagram,
          hideLabel: hasComponentEdge.in.hideLabel,
          hideDiagram: hasComponentEdge.in.hideDiagram,
          gridShell: !ctrl.useGeoPositioning,
          formula: hasComponentEdge.in.formula,
          pointsLinked: hasComponentEdge.in.pointsLinked,
          staticSvgs: staticSvgs,
          pointsLinkedData: {
            pointsLinked: hasComponentEdge.in.pointsLinked,
            pointsLinkedMinValue: hasComponentEdge.in.minValue,
            pointsLinkedMaxValue: hasComponentEdge.in.maxValue,
            pointsLinkeddirection: hasComponentEdge.in.direction,
            nodeKey: hasComponentEdge.in['@rid'].toString(),
            formula: hasComponentEdge.in.formula,
            staticSvgs: staticSvgs,
            equipmentName: hasComponentEdge.in.name,
          },
        };

        //Edge case Fans
        if (hasComponentEdge.in['@class'].includes('Fan') || hasComponentEdge.in['@class'].includes('Pump')) {
          node.svgBackground = hasComponentEdge.in.svgBackground;
          node.svgBackgroundConverted = hasComponentEdge.in.svgBackgroundConverted;
          node.svgForeground = hasComponentEdge.in.svgForeground;
          node.svgForegroundConverted = hasComponentEdge.in.svgForegroundConverted;
          node.rotatorDashArray = hasComponentEdge.in.rotatorDashArray;
          node.rotatorColor = hasComponentEdge.in.rotatorColor;
          node.hideRotator = hasComponentEdge.in.hideRotator;
        }

        if (hasComponentEdge.in.latitude && hasComponentEdge.in.longitude && ctrl.useGeoPositioning) {
          node.latlong = [hasComponentEdge.in.latitude, hasComponentEdge.in.longitude]
        } else {
          node.latlong = []
          if (hasComponentEdge.in.diagramData && hasComponentEdge.in.diagramData[ctrl.equipmentRecord()['@rid']] && hasComponentEdge.in.diagramData[ctrl.equipmentRecord()['@rid']]) {
            node.latlong = hasComponentEdge.in.diagramData[ctrl.equipmentRecord()['@rid']]?.loc?.split(' ').map(parseFloat);
          }
        }

        if (hasComponentEdge.in.svg) {
          node.svg = hasComponentEdge.in.svg;
        }
        if (hasComponentEdge.in.svgConverted) {
          node.svgConverted = hasComponentEdge.in.svgConverted;
        }

        if (hasComponentEdge.in.svgBackground) {
          node.svgBackground = hasComponentEdge.in.svgBackground;
        }
        if (hasComponentEdge.in.svgBackgroundConverted) {
          node.svgBackgroundConverted = hasComponentEdge.in.svgBackgroundConverted;
        }

        if (hasComponentEdge.in.svgForeground) {
          node.svgForeground = hasComponentEdge.in.svgForeground;
        }
        if (hasComponentEdge.in.svgForegroundConverted) {
          node.svgForegroundConverted = hasComponentEdge.in.svgForegroundConverted;
        }

        if (hasComponentEdge.in.rotatorDashArray) {
          node.rotatorDashArray = hasComponentEdge.in.rotatorDashArray;
        }
        if (hasComponentEdge.in.rotatorColor) {
          node.rotatorColor = hasComponentEdge.in.rotatorColor;
        }
        if (hasComponentEdge.in.hideRotator) {
          node.hideRotator = hasComponentEdge.in.hideRotator;
        }


        if (hasComponentEdge.in.ports) {
          node.ports = hasComponentEdge.in.ports;
          Object.values(hasComponentEdge.in.ports).forEach(port => {
            if (port.position == 'left') {
              node.leftArray.push({ portColor: diagramConfiguration.findPortColor(serviceTaxonomy.getServiceClass(port)), portName: port.name, portId: `Port_${port.id}`, type: ctrl.isEditingEnabled() ? port.type : false, category: serviceTaxonomy.getServiceClass(port) })
            }
            if (port.position == 'right') {
              node.rightArray.push({ portColor: diagramConfiguration.findPortColor(serviceTaxonomy.getServiceClass(port)), portName: port.name, portId: `Port_${port.id}`, type: ctrl.isEditingEnabled() ? port.type : false, category: serviceTaxonomy.getServiceClass(port) })
            }
            if (port.position == 'top') {
              node.topArray.push({ portColor: diagramConfiguration.findPortColor(serviceTaxonomy.getServiceClass(port)), portName: port.name, portId: `Port_${port.id}`, type: ctrl.isEditingEnabled() ? port.type : false, category: serviceTaxonomy.getServiceClass(port) })
            }
            if (port.position == 'bottom') {
              node.bottomArray.push({ portColor: diagramConfiguration.findPortColor(serviceTaxonomy.getServiceClass(port)), portName: port.name, portId: `Port_${port.id}`, type: ctrl.isEditingEnabled() ? port.type : false, category: serviceTaxonomy.getServiceClass(port) })
            }
            if (port.position == 'center') {
              node.centerArray.push({ portColor: diagramConfiguration.findPortColor(serviceTaxonomy.getServiceClass(port)), portName: port.name, portId: `Port_${port.id}`, type: ctrl.isEditingEnabled() ? port.type : false, category: serviceTaxonomy.getServiceClass(port) })
            }
          })
        }

        if (node.class != 'Manifold') {
          node.centerArray.push({ portColor: controlsEdgeColor, portName: 'Controls', portId: `Controls_0`, type: 'Controls', category: 'Controls', linkColor: controlsEdgeColor });
        }

        if (hasComponentEdge.in.showInSummary) {
          node.showInSummary = hasComponentEdge.in.showInSummary;
        }


        const equipmentDiagramData = hasComponentEdge.in['diagramData'] && hasComponentEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']];
        equipmentDiagramData && equipmentDiagramData.width ? node.width = equipmentDiagramData.width : '';
        equipmentDiagramData && equipmentDiagramData.height ? node.height = equipmentDiagramData.height : '';
        equipmentDiagramData && equipmentDiagramData.cols ? node.cols = equipmentDiagramData.cols : '';
        equipmentDiagramData && equipmentDiagramData.rows ? node.rows = equipmentDiagramData.rows : '';
        equipmentDiagramData && equipmentDiagramData.loc ? node.loc = equipmentDiagramData.loc : '';

        applyDefaultGridShellSizeForPorts(node);

        return node;
      });
    (ctrl.isDiagram || ctrl.useGeoPositioning ? [] : ctrl.equipmentRecord().out_Contains || [])
      .filter(containsEdge => shouldIncludeDirectDatasetInDiagram(containsEdge))
      .forEach(containsEdge => {
      (containsEdge.in['columns'] || []).forEach(c => {
        let out_Column = (containsEdge.in['out_Column'] || []).find(outColumn => outColumn.name == c.name);
        if (out_Column) {
          Object.assign(c, out_Column);
        }
        c.datasetRid = containsEdge.in['@rid'];
        c.parentEquipmentRid = ctrl.equipmentRecord()['@rid']; 
      });
      let node = {
        category: 'Dataset',
        key: containsEdge.in['@rid'],
        name: containsEdge.in['name'] == 'Default' ? ctrl.equipmentRecord().name : `${ctrl.equipmentRecord().name || ctrl.equipmentRecord()['@class']} - ${containsEdge.in['name']}`,
        columns: containsEdge.in['columns'],
        diagramData: containsEdge.in.diagramData,
        isDefaulDataset: containsEdge.in['name'] == 'Default' ? true : false,
        parentRecordRid: ctrl.equipmentRecord()['@rid'],
        definedByParentEquipmentClass: containsEdge.in.definedByParentEquipmentClass,
      };
      containsEdge.in['diagramData'] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].width ? node.width = containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].width : '';
      containsEdge.in['diagramData'] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].height ? node.height = containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].height : '';
      //containsEdge.in['diagramData'] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].loc ? node.loc = containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].loc : '';
      node.latlong = []
      if (containsEdge.in['diagramData'] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']]) {
        node.latlong = containsEdge.in['diagramData'] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].loc.split(' ').map(parseFloat);
      }

      ctrl.nodeDirectDatasetArray.push(node);
    }),
      (ctrl.isDiagram || ctrl.useGeoPositioning ? [] : ctrl.equipmentRecord().out_containsLocation || []).forEach(containsLocationEdge => {
        let node = {
          category: 'Room',
          class: containsLocationEdge.in['@class'],
          key: containsLocationEdge.in['@rid'],
          name: containsLocationEdge.in['name'],
          linkKey: containsLocationEdge['@rid'],
          linkCategory: containsLocationEdge['@class'],
          diagramData: containsLocationEdge.in.diagramData,
          leftArray: [],
          rightArray: [],
          topArray: [],
          bottomArray: [],
          centerArray: [],
          data: { ...containsLocationEdge.in.data },
          parentLocation: containsLocationEdge.in.in_hasPart[0].out,
          roomRecord: containsLocationEdge.in,
          datasetsRids: (containsLocationEdge.in.out_Contains || []).map(c => c.in['@rid'])
        };

        if (containsLocationEdge.in.latitude && containsLocationEdge.in.longitude && ctrl.useGeoPositioning) {
          node.latlong = [containsLocationEdge.in.latitude, containsLocationEdge.in.longitude]
        } else {
          node.latlong = []
          if (containsLocationEdge.in.diagramData && containsLocationEdge.in.diagramData[ctrl.equipmentRecord()['@rid']] && containsLocationEdge.in.diagramData[ctrl.equipmentRecord()['@rid']]) {
            node.latlong = containsLocationEdge.in.diagramData[ctrl.equipmentRecord()['@rid']]?.loc?.split(' ').map(parseFloat);
          }
        }

        if (containsLocationEdge.in.diagramSvg) {
          node.diagramSvg = containsLocationEdge.in.diagramSvg;
        }
        if (containsLocationEdge.in.diagramSvgConverted) {
          node.diagramSvgConverted = containsLocationEdge.in.diagramSvgConverted;
        }
        if (containsLocationEdge.in.ports) {
          node.ports = containsLocationEdge.in.ports;
          Object.values(containsLocationEdge.in.ports).forEach(port => {
            if (port.position == 'left') {
              node.leftArray.push({ portColor: diagramConfiguration.findPortColor(serviceTaxonomy.getServiceClass(port)), portName: port.name, portId: `Port_${port.id}`, type: ctrl.isEditingEnabled() ? port.type : false, category: serviceTaxonomy.getServiceClass(port) })
            }
            if (port.position == 'right') {
              node.rightArray.push({ portColor: diagramConfiguration.findPortColor(serviceTaxonomy.getServiceClass(port)), portName: port.name, portId: `Port_${port.id}`, type: ctrl.isEditingEnabled() ? port.type : false, category: serviceTaxonomy.getServiceClass(port) })
            }
            if (port.position == 'top') {
              node.topArray.push({ portColor: diagramConfiguration.findPortColor(serviceTaxonomy.getServiceClass(port)), portName: port.name, portId: `Port_${port.id}`, type: ctrl.isEditingEnabled() ? port.type : false, category: serviceTaxonomy.getServiceClass(port) })
            }
            if (port.position == 'bottom') {
              node.bottomArray.push({ portColor: diagramConfiguration.findPortColor(serviceTaxonomy.getServiceClass(port)), portName: port.name, portId: `Port_${port.id}`, type: ctrl.isEditingEnabled() ? port.type : false, category: serviceTaxonomy.getServiceClass(port) })
            }
            if (port.position == 'center') {
              node.centerArray.push({ portColor: diagramConfiguration.findPortColor(serviceTaxonomy.getServiceClass(port)), portName: port.name, portId: `Port_${port.id}`, type: ctrl.isEditingEnabled() ? port.type : false, category: serviceTaxonomy.getServiceClass(port) })
            }
          })
        }

        containsLocationEdge.in['diagramData'] && containsLocationEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']] && containsLocationEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].width ? node.width = containsLocationEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].width : '';
        containsLocationEdge.in['diagramData'] && containsLocationEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']] && containsLocationEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].height ? node.height = containsLocationEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].height : '';
        containsLocationEdge.in['diagramData'] && containsLocationEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']] && containsLocationEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].cols ? node.cols = containsLocationEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].cols : '';
        containsLocationEdge.in['diagramData'] && containsLocationEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']] && containsLocationEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].rows ? node.rows = containsLocationEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].rows : '';
        containsLocationEdge.in['diagramData'] && containsLocationEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']] && containsLocationEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].loc ? node.loc = containsLocationEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].loc : '';

        applyDefaultGridShellSizeForPorts(node);

        ctrl.nodeLocationArray.push(node);

        (containsLocationEdge.in.out_Contains || []).forEach(containsEdge => {
          if (!shouldIncludeParentDatasetInDiagram(containsEdge, ctrl.equipmentRecord()['@rid'])) {
            return;
          }
          (containsEdge.in['columns'] || []).forEach(c => {
            let out_Column = (containsEdge.in['out_Column'] || []).find(outColumn => outColumn.name == c.name);
            if (out_Column) {
              Object.assign(c, out_Column);
            }
            c.datasetRid = containsEdge.in['@rid'];
            c.parentEquipmentRid = containsLocationEdge.in['@rid'];
          });
          let node = {
            category: 'Dataset',
            key: containsEdge.in['@rid'],
            name: containsEdge.in['name'] == 'Default' ? containsLocationEdge.in.name : `${containsLocationEdge.in.name} - ${containsEdge.in['name']}`,
            columns: containsEdge.in['columns'],
            diagramData: containsEdge.in.diagramData,
            isDefaulDataset: containsEdge.in['name'] == 'Default' ? true : false,
            parentRecordRid: containsLocationEdge.in['@rid'],
            definedByParentEquipmentClass: containsEdge.in.definedByParentEquipmentClass,
          };
          containsEdge.in['diagramData'] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].width ? node.width = containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].width : '';
          containsEdge.in['diagramData'] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].height ? node.height = containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].height : '';
          containsEdge.in['diagramData'] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].loc ? node.loc = containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].loc : '';
          node.latlong = []
          if (containsEdge.in['diagramData'] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']]) {
            node.latlong = containsEdge.in['diagramData'] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].loc.split(' ').map(parseFloat);
          }
          ctrl.nodeDatasetArray.push(node);
        }),
          (containsLocationEdge.in.out_Feeds || []).forEach(feedsEdge => {
            const currentToPortKey = normalizeConnectionPortId(feedsEdge.toPortId, ctrl.equipmentRecord().ports);
            let linkFeedsData = {
              class: feedsEdge['@class'],
              category: serviceTaxonomy.getServiceClass(feedsEdge),
              key: feedsEdge['@rid'],
              from: getRecordRid(feedsEdge.out),
              to: isSameRid(feedsEdge.in, ctrl.equipmentRecord()['@rid']) && currentToPortKey ? currentToPortKey : getRecordRid(feedsEdge.in),
              fromPortId: feedsEdge.fromPortId,
              toPortId: feedsEdge.toPortId,
              diagramData: feedsEdge.diagramData,
              linkColor: diagramConfiguration.findPortColor(serviceTaxonomy.getServiceClass(feedsEdge)),
              routing: ctrl.useGeoPositioning ? go.Link.Normal : go.Link.Orthogonal,
              minValue: feedsEdge.minValue,
              maxValue: feedsEdge.maxValue,
              pointsLinked: feedsEdge.pointsLinked || [],
              equipmentDiagramRid: ctrl.equipmentRecord()['@rid'],
              formula: feedsEdge.formula
            }
            let linkDashPattern = diagramConfiguration.findLinkDashArray(serviceTaxonomy.getServiceClass(feedsEdge));
            if (linkDashPattern) {
              linkFeedsData.dashPattern = linkDashPattern;
            } else {
              linkFeedsData.dashPattern = [5, 5];
            }
            applyStoredRoute(linkFeedsData, feedsEdge, ctrl.equipmentRecord()['@rid'], ctrl.useGeoPositioning);
            ctrl.linkFeedsDataArray.push(linkFeedsData)
          })
      }),
      (childEquipment || []).forEach(hasComponentEdge => {
        let childEquipmentRecord = hasComponentEdge.in;
        (childEquipmentRecord.out_Contains || [])
          .filter(containsEdge => shouldIncludeParentDatasetInDiagram(containsEdge, ctrl.equipmentRecord()['@rid']))
          .forEach(containsEdge => {
          (containsEdge.in['columns'] || []).forEach(c => {
            let out_Column = (containsEdge.in['out_Column'] || []).find(outColumn => outColumn.name == c.name);
            if (out_Column) {
              Object.assign(c, out_Column);
            }
            c.datasetRid = containsEdge.in['@rid'];
            c.parentEquipmentRid = childEquipmentRecord['@rid'];
          });

          let node = {
            category: 'Dataset',
            key: containsEdge.in['@rid'],
            name: containsEdge.in['name'] == 'Default' ? `${childEquipmentRecord.name ? childEquipmentRecord.name : childEquipmentRecord['@class']}` : `${childEquipmentRecord.name || childEquipmentRecord['@class']} - ${containsEdge.in['name']}`,
            columns: containsEdge.in['columns'],
            diagramData: containsEdge.in.diagramData,
            isDefaulDataset: containsEdge.in['name'] == 'Default' ? true : false,
            parentRecordRid: childEquipmentRecord['@rid'],
            definedByParentEquipmentClass: containsEdge.in.definedByParentEquipmentClass,
          };

          containsEdge.in['diagramData'] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].width ? node.width = containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].width : '';
          containsEdge.in['diagramData'] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].height ? node.height = containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].height : '';

          node.latlong = []
          if (containsEdge.in['diagramData'] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].loc) {
            node.latlong = containsEdge.in['diagramData'] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']] && containsEdge.in['diagramData'][ctrl.equipmentRecord()['@rid']].loc.split(' ').map(parseFloat);

          } else {
            if (ctrl.useGeoPositioning && ctrl.map) {
              node.latlong = [ctrl.map._lastCenter.lat, ctrl.map._lastCenter.lng];
            } else {
              node.latlong = [0, 0];
            }
          }

          ctrl.nodeDatasetArray.push(node);

          if (ctrl.isEditingEnabled()) {
            let linkContainsData = {
              class: containsEdge['@class'],
              category: containsEdge.category,
              from: node.key,
              to: childEquipmentRecord['@rid'],
              linkColor: '#2986cc',
              key: containsEdge['@rid'],
              diagramData: containsEdge.diagramData,
              routing: go.Link.Normal
            }

            applyStoredRoute(linkContainsData, containsEdge, ctrl.equipmentRecord()['@rid'], ctrl.useGeoPositioning);
            ctrl.linkContainsDataArray.push(linkContainsData);
          }
        }),
          (ctrl.isDiagram ? [] : childEquipmentRecord.out_Feeds || []).forEach(feedsEdge => {
            const currentToPortKey = normalizeConnectionPortId(feedsEdge.toPortId, ctrl.equipmentRecord().ports);
            let linkFeedsData = {
              class: feedsEdge['@class'],
              category: serviceTaxonomy.getServiceClass(feedsEdge),
              key: feedsEdge['@rid'],
              from: getRecordRid(feedsEdge.out),
              to: isSameRid(feedsEdge.in, ctrl.equipmentRecord()['@rid']) && currentToPortKey ? currentToPortKey : getRecordRid(feedsEdge.in),
              fromPortId: feedsEdge.fromPortId,
              toPortId: feedsEdge.toPortId,
              diagramData: feedsEdge.diagramData,
              linkColor: diagramConfiguration.findPortColor(serviceTaxonomy.getServiceClass(feedsEdge)),
              routing: ctrl.useGeoPositioning ? go.Link.Normal : go.Link.Orthogonal,
              minValue: feedsEdge.minValue,
              maxValue: feedsEdge.maxValue,
              pointsLinked: feedsEdge.pointsLinked || [],
              equipmentDiagramRid: ctrl.equipmentRecord()['@rid'],
              formula: feedsEdge.formula
            }
            let linkDashPattern = diagramConfiguration.findLinkDashArray(serviceTaxonomy.getServiceClass(feedsEdge));
            if (linkDashPattern) {
              linkFeedsData.dashPattern = linkDashPattern;
            } else {
              linkFeedsData.dashPattern = [5, 5];
            }
            applyStoredRoute(linkFeedsData, feedsEdge, ctrl.equipmentRecord()['@rid'], ctrl.useGeoPositioning);

            ctrl.linkFeedsDataArray.push(linkFeedsData)
          }),
          (ctrl.isDiagram ? [] : childEquipmentRecord.out_Controls || []).forEach(controlsEdge => {
            const currentToPortKey = normalizeConnectionPortId(controlsEdge.toPortId, ctrl.equipmentRecord().ports);
            let linkControlsData = {
              class: controlsEdge['@class'],
              category: 'Controls',
              key: controlsEdge['@rid'],
              from: getRecordRid(controlsEdge.out),
              to: isSameRid(controlsEdge.in, ctrl.equipmentRecord()['@rid']) && currentToPortKey ? currentToPortKey : getRecordRid(controlsEdge.in),
              fromPortId: controlsEdge.fromPortId,
              toPortId: controlsEdge.toPortId,
              linkColor: controlsEdgeColor,
              labelText: 'Control',
            }

            applyStoredRoute(linkControlsData, controlsEdge, ctrl.equipmentRecord()['@rid'], ctrl.useGeoPositioning);
            ctrl.linkControlsDataArray.push(linkControlsData)
          })
      });
    if (ctrl.isDiagram) {
      addDiagramFeedLinks(ctrl, childEquipment);
    }
    (ctrl.isDiagram || !ctrl.equipmentRecord().ports || ctrl.useGeoPositioning ? [] : Object.values(ctrl.equipmentRecord().ports)).forEach(port => {
        let portNode = {
          category: 'Port',
          key: `Port_${port.id}`,
          name: port.name,
          type: port.type,
          color: diagramConfiguration.findPortColor(serviceTaxonomy.getServiceClass(port)),
          parentRecordPorts: ctrl.equipmentRecord().ports,
          parentRid: ctrl.equipmentRecord()['@rid'],
          direction: port.direction,
          directionAndType: [port.direction, port.type],
          class: serviceTaxonomy.getServiceClass(port), serviceClass: serviceTaxonomy.getServiceClass(port),
        }

        // Determine all linked equipments for this port (handle 1:N or N:M)
        const linkedEquipments = [];
        // Build detailed linked connections (with feedsPaths like the Connections tab)
        const linkedConnections = [];
        const addTarget = (target) => {
          if (!target) return;
          const rid = typeof target === 'object' ? target['@rid'] : target;
          const klass = typeof target === 'object' ? target['@class'] : undefined;
          const name = typeof target === 'object' ? target['name'] : undefined;
          if (!rid || !klass) return;
          // de-dupe by rid
          if (!linkedEquipments.find(le => le.rid === rid)) linkedEquipments.push({ rid, class: klass, name });
        };

        if (port.type === 'output' || port.type === 'bidirectional') {
          (ctrl.equipmentRecord().out_Feeds || [])
            .filter(e => e && connectionMatchesPort(port, e.fromPortId))
            .forEach(e => {
              addTarget(e.in);
              // include detailed connection for menu rendering
              const other = (typeof e.in === 'object') ? e.in : { '@rid': e.in };
              linkedConnections.push({
                direction: 'out',
                other,
                feedsPaths: Array.isArray(e.feedsPaths) ? e.feedsPaths : []
              });
            });
        }
        if (port.type === 'input' || port.type === 'bidirectional') {
          (ctrl.equipmentRecord().in_Feeds || [])
            .filter(e => e && connectionMatchesPort(port, e.toPortId))
            .forEach(e => {
              addTarget(e.out);
              const other = (typeof e.out === 'object') ? e.out : { '@rid': e.out };
              linkedConnections.push({
                direction: 'in',
                other,
                feedsPaths: Array.isArray(e.feedsPaths) ? e.feedsPaths : []
              });
            });
        }

        if (linkedEquipments.length > 0) {
          portNode.linkedEquipments = linkedEquipments;
        }
        if (linkedConnections.length > 0) {
          portNode.linkedConnections = linkedConnections;
        }
        

        if (portNode.direction && (port.type == 'input' || port.type == 'output')) {
          if (port.direction == 'left') {
            if (port.connector && port.connector == 'head') {
              portNode.leftArray = [{ portColor: diagramConfiguration.findPortColor(serviceTaxonomy.getServiceClass(port)), portName: port.name, portId: port.id.toString(), type: port.type == 'input' ? 'output' : 'input', category: serviceTaxonomy.getServiceClass(port) }]
              portNode.connector = 'head';
            } else {
              portNode.rightArray = [{ portColor: diagramConfiguration.findPortColor(serviceTaxonomy.getServiceClass(port)), portName: port.name, portId: port.id.toString(), type: port.type == 'input' ? 'output' : 'input', category: serviceTaxonomy.getServiceClass(port) }]
              portNode.connector = 'tail';
            }
          } else {
            if (port.connector && port.connector == 'head') {
              portNode.rightArray = [{ portColor: diagramConfiguration.findPortColor(serviceTaxonomy.getServiceClass(port)), portName: port.name, portId: port.id.toString(), type: port.type == 'input' ? 'output' : 'input', category: serviceTaxonomy.getServiceClass(port) }]
              portNode.connector = 'head';
            } else {
              portNode.leftArray = [{ portColor: diagramConfiguration.findPortColor(serviceTaxonomy.getServiceClass(port)), portName: port.name, portId: port.id.toString(), type: port.type == 'input' ? 'output' : 'input', category: serviceTaxonomy.getServiceClass(port) }]
              portNode.connector = 'tail';
            }
          }
        } else {
          portNode.rightArray = [{ portColor: diagramConfiguration.findPortColor(serviceTaxonomy.getServiceClass(port)), portName: port.name, portId: port.id.toString(), type: port.type == 'input' ? 'output' : 'input', category: serviceTaxonomy.getServiceClass(port) }];
        }
        if (port.type == 'bidirectional') {
          portNode.topArray = [{ portColor: diagramConfiguration.findPortColor(serviceTaxonomy.getServiceClass(port)), portName: port.name, portId: port.id.toString(), type: 'bidirectional', category: serviceTaxonomy.getServiceClass(port) }]
        }

        port.latlong = []
        if (port.loc) {
          portNode.latlong = port.loc.split(' ').map(parseFloat);
        }

        ctrl.nodePortArray.push(portNode);

      }),
      (ctrl.isDiagram || !ctrl.equipmentRecord().out_Feeds || ctrl.useGeoPositioning ? [] : ctrl.equipmentRecord().out_Feeds).forEach(feedsEdge => {
        const currentFromPortKey = normalizeConnectionPortId(feedsEdge.fromPortId, ctrl.equipmentRecord().ports);
        var from = isSameRid(feedsEdge.out, ctrl.equipmentRecord()['@rid']) && currentFromPortKey ? currentFromPortKey : getRecordRid(feedsEdge.out);
        let linkFeedsData = {
          class: feedsEdge['@class'],
          category: serviceTaxonomy.getServiceClass(feedsEdge),
          key: feedsEdge['@rid'],
          from: from,
          to: getRecordRid(feedsEdge.in),
          fromPortId: feedsEdge.fromPortId,
          toPortId: feedsEdge.toPortId,
          diagramData: feedsEdge.diagramData,
          linkColor: diagramConfiguration.findPortColor(serviceTaxonomy.getServiceClass(feedsEdge)),
          routing: ctrl.useGeoPositioning ? go.Link.Normal : go.Link.Orthogonal,
          minValue: feedsEdge.minValue,
          maxValue: feedsEdge.maxValue,
          pointsLinked: feedsEdge.pointsLinked || [],
          equipmentDiagramRid: ctrl.equipmentRecord()['@rid'],
          formula: feedsEdge.formula
        }
        let linkDashPattern = diagramConfiguration.findLinkDashArray(serviceTaxonomy.getServiceClass(feedsEdge));
        if (linkDashPattern) {
          linkFeedsData.dashPattern = linkDashPattern;
        } else {
          linkFeedsData.dashPattern = [5, 5];
        }
        applyStoredRoute(linkFeedsData, feedsEdge, ctrl.equipmentRecord()['@rid'], ctrl.useGeoPositioning);
        ctrl.linkFeedsDataArray.push(linkFeedsData)
      })

    ctrl.groupsArray = (ctrl.equipmentRecord().groups || []).map(group => {
      return {
        category: 'Group',
        key: group.id,
        latlong: group.loc ? group.loc.split(' ').map(parseFloat) : [],
        width: group.width,
        height: group.height,
        color: '#D3D3D3',
        name: group.name,
        parentRecordGroups: ctrl.equipmentRecord().groups,
        parentRid: ctrl.equipmentRecord()['@rid'],
      };
    });

    createAndSaveDiagram(ctrl);
    vnode.attrs.diagram(ctrl.diagram);
  }

  ctrl.init();
  if (ctrl.parentCtrl?.setViewportAndAdjustBounds) ctrl.parentCtrl.setViewportAndAdjustBounds();
  ctrl.updateLinkAnimation();
}

var createAndSaveDiagram = function (ctrl) {
  class CustomCommandHandler extends go.CommandHandler {
    constructor() {
      super();
    }

    copySelection() {
      let diagram = this.diagram;
      if (diagram === null || ctrl.useGeoPositioning) return;
      let selectedData = diagram.selection.toArray().map(part => {
        let nodePart = {
          category: isEquipmentPartDataForClipboard(part.data) ? 'Equipment' : part.data.category,
          class: part.data.class,
          key: part.data.key,
        };
        if (part.data.name) nodePart.name = part.data.name;
        if (part.data.loc) nodePart.loc = part.data.loc;
        if (part.data.diagramData) {
          nodePart.diagramData = part.data.diagramData;
        }

        if (isEquipmentPartDataForClipboard(part.data)) {
          nodePart.data = part.data.data && typeof part.data.data === 'object' ? { ...part.data.data } : {};
          if (part.data.datasetsRids) {
            nodePart.datasetsRids = part.data.datasetsRids;
          }
          if (part.data.ports) {
            nodePart.ports = part.data.ports;
          }
          if (part.data.svg) {
            nodePart.data.svg = part.data.svg;
          }
          if (part.data.svgConverted) {
            nodePart.data.svgConverted = part.data.svgConverted;
          }
          if (part.data.svgBackground) {
            nodePart.data.svgBackground = part.data.svgBackground;
          }
          if (part.data.svgBackgroundConverted) {
            nodePart.data.svgBackgroundConverted = part.data.svgBackgroundConverted;
          }
          if (part.data.svgForeground) {
            nodePart.data.svgForeground = part.data.svgForeground;
          }
          if (part.data.svgForegroundConverted) {
            nodePart.data.svgForegroundConverted = part.data.svgForegroundConverted;
          }
          if (part.data.rotatorDashArray) {
            nodePart.data.rotatorDashArray = part.data.rotatorDashArray;
          }
          if (part.data.rotatorColor) {
            nodePart.data.rotatorColor = part.data.rotatorColor;
          }
          if (part.data.hideRotator) {
            nodePart.data.hideRotator = part.data.hideRotator;
          }
          ontologyFields.normalizeFieldDefinitions(nodePart.data.fields || []).forEach(field => {
            var fieldName = ontologyFields.getFieldName(field);
            var fieldValue = part.data[fieldName];
            if (ontologyFields.isDisplayableFieldValue(fieldValue, field)) {
              nodePart[fieldName] = cloneJson(fieldValue);
            }
          });
          Object.keys(customEquimentModel.fields || {}).forEach(key => {
            if (part.data[key]) {
              nodePart[key] = part.data[key];
            }
          })
        }

        if (part.data.category === 'Port') {
          if (part.data.direction) {
            nodePart.direction = part.data.direction;
          }
          nodePart.type = part.data.type;
          nodePart.loc = part.data.latlong.join(' ');
          nodePart.name = part.data.name;
          nodePart.connector = part.data.connector;
        }

        if (part.data.class === 'Feeds') {
          if (part.data.from) {
            nodePart.from = part.data.from.includes('Port') ? 'ParentEquipment' : part.data.from;
          }
          if (part.data.fromPortId) {
            nodePart.fromPortId = part.data.fromPortId;
          }
          if (part.data.to) {
            nodePart.to = part.data.to.includes('Port') ? 'ParentEquipment' : part.data.to;
          }
          if (part.data.toPortId) {
            nodePart.toPortId = part.data.toPortId;
          }
        }

        if (part.data.category === 'Dataset') {
          nodePart.columns = part.data.columns;
          nodePart.type = part.data.type;
          nodePart.parentRecordRid = part.data.parentRecordRid;
          nodePart.definedByParentEquipmentClass = part.data.definedByParentEquipmentClass;
          const storedLocation = getNodeDiagramPoint(part.data, ctrl.equipmentRecord()['@rid']);
          nodePart.loc = storedLocation ? formatDiagramPoint(storedLocation) : part.data.loc;
          nodePart.isDefaulDataset = part.data.isDefaulDataset;
          nodePart.name = part.data.name;
        }

        return nodePart;
      });

      let serializedData = JSON.stringify(selectedData);
      ctrl.lastCopiedSelection = selectedData;
      super.copySelection();

      if (navigator.clipboard) {
        setTimeout(function () {
          navigator.clipboard.writeText(serializedData)
            .then(() => {
              // Success callback
            }, err => {
              console.error('Could not copy text: ', err);
            });
        }, 25);
      }
    }
  }

  function straightenNearlyAlignedExternalFlagLink(link) {
    if (!link || !link.points || link.points.count < 2) return;
    normalizeLiveDiagramLinkPoints(link, getGoLinkRouteKind(link));
  }

  function alignStoredFeedLinkEndpointsToPorts() {
    if (ctrl.useGeoPositioning || !ctrl.diagram) return;
    ctrl.diagram.skipsUndoManager = true;
    try {
      ctrl.diagram.startTransaction('Align Stored Feed Endpoints');
      ctrl.diagram.links.each(link => {
        sanitizeLiveLinkPoints(link, getGoLinkRouteKind(link));
        alignFeedLinkEndpointsToPorts(link);
        straightenNearlyAlignedExternalFlagLink(link);
      });
      ctrl.diagram.commitTransaction('Align Stored Feed Endpoints');
    } finally {
      ctrl.diagram.skipsUndoManager = false;
    }
  }

  const $ = go.GraphObject.make;
  ctrl.diagram = new go.Diagram(ctrl.canvasId, {
    "InitialLayoutCompleted": e => {
      // Update animated strokes for fans
      ctrl.diagram.nodes.each((node) => {
        if (node.data.category == 'Fan') {
          const strokeShape = node.findObject("animated-stroke");
          let inPort = Object.values(node.data.ports).find(p => p.type == 'input');
          let color = diagramConfiguration.findPortColor(inPort.class);
          if (strokeShape && color) {
            strokeShape.stroke = color;
          }
          let strokeDashArray = diagramConfiguration.findLinkDashArray(inPort.class);
          if (strokeShape && strokeDashArray) {
            strokeShape.strokeDashArray = strokeDashArray;
          }

        }
      })
      if (!ctrl.useGeoPositioning) {
        alignStoredFeedLinkEndpointsToPorts();
      }
      if (ctrl.useGeoPositioning) {
        ctrl.updatePosition();
      } else {
        ctrl.diagram.zoomToFit();
        ctrl.diagram.alignDocument(go.Spot.Center, go.Spot.Center);
      }
      selectPendingEquipmentGroup();
    },
    "draggingTool.isGridSnapEnabled": true,
    "resizingTool.isGridSnapEnabled": true,
    "rotatingTool.snapAngleMultiple": 90,
    "rotatingTool.snapAngleEpsilon": 45,
    "contextMenuTool.defaultTouchContextMenu": null,
    commandHandler: new CustomCommandHandler(),
    dragSelectingTool: $(RealtimeDragSelectingTool, {
      isPartialInclusion: true, // Allow partial selection
      delay: 500, // Delay before starting selection
    }, {
      box: $(go.Part, {
        layerName: "Tool",
        selectable: false,
      }, $(go.Shape, {
        name: "SHAPE",
        fill: "rgba(255,255,255,0.1)", // Rectangle fill color
        stroke: "white", // Rectangle border color
        strokeWidth: 2,
      }))
    }),
    scrollMode: ctrl.isEditingEnabled() ? go.Diagram.InfiniteScroll : go.Diagram.DocumentScroll,
    allowZoom: !ctrl.useGeoPositioning,
    allowHorizontalScroll: ctrl.isEditingEnabled() ? true : false,
    allowVerticalScroll: ctrl.isEditingEnabled() ? true : false,
    maxScale: 2,
    minScale: 0.1,
    hasHorizontalScrollbar: !ctrl.useGeoPositioning,
    hasVerticalScrollbar: !ctrl.useGeoPositioning,
    padding: 0,
    defaultCursor: "default",
    "toolManager.hoverDelay": 100,  // how quickly tooltips are shown
    "undoManager.isEnabled": true,
    "ModelChanged": e => {
      if (e.isTransactionFinished && e.oldValue == "Initial Layout") {
        ctrl.updateLinkAnimation();
      }
      if (e.change === go.ChangedEvent.Transaction &&
        (e.propertyName === "FinishedUndo" || e.propertyName === "FinishedRedo")) {
      }
    },
  });
  ctrl.diagram.pointSubscriber = ctrl.pointSubscriberObj;
  ctrl.diagram.pointSubscriberObj = ctrl.pointSubscriberObj;

  ctrl.diagram.commandHandler.doKeyDown = function () {
    const diagram = this.diagram;
    const e = diagram.lastInput;
    const control = e.meta || e.control;
    const shift = e.shift;
    const key = e.key;
    if ((control && key === 'V') || (shift && key === 'Insert')) {
      e.bubbles = true;
    } else {
      go.CommandHandler.prototype.doKeyDown.call(this, e);
    }
  }
  // Get the diagram container
  const diagramDiv = document.getElementById(ctrl.canvasId);


  // Function to enable scrolling
  function enableScrolling() {
    if (ctrl.diagram) {
      ctrl.diagram.startTransaction("EnableScrolling");
      ctrl.diagram.allowHorizontalScroll = true;
      ctrl.diagram.allowVerticalScroll = true;
      if (ctrl.forceReadOnly) {
        ctrl.diagram.toolManager.mouseWheelBehavior = go.ToolManager.WheelZoom;
      }
      ctrl.diagram.commitTransaction("EnableScrolling");
    }
  }

  // Function to disable scrolling
  function disableScrolling() {
    if (ctrl.diagram) {
      ctrl.diagram.startTransaction("DisableScrolling");
      ctrl.diagram.allowHorizontalScroll = false;
      ctrl.diagram.allowVerticalScroll = false;
      if (ctrl.forceReadOnly) {
        ctrl.diagram.toolManager.mouseWheelBehavior = go.ToolManager.WheelNone;
      }
      ctrl.diagram.commitTransaction("DisableScrolling");
    }
  }

  // Function to add a border to the diagram container using box-shadow
  function addDiagramBorder() {
    if (diagramDiv) {
      diagramDiv.style.boxShadow = "0 0 0 2px #58ABE2"; // Blue border effect
      diagramDiv.style.borderRadius = "4px"; // Optional: Add rounded corners
    }
  }

  // Function to remove the border from the diagram container
  function removeDiagramBorder() {
    if (diagramDiv) {
      diagramDiv.style.boxShadow = "none";
    }
  }

  function getClickedDiagramNode(e) {
    const subject = e && e.subject;
    const subjectPart = subject instanceof go.Part ? subject : subject && subject.part;
    const inputTarget = e && e.diagram && e.diagram.lastInput && e.diagram.lastInput.targetObject;
    const inputTargetPart = inputTarget && inputTarget.part;
    const part = subjectPart || inputTargetPart;
    const adornedPart = part instanceof go.Adornment ? part.adornedPart : null;
    const clickedPart = adornedPart || part;
    return clickedPart instanceof go.Node ? clickedPart : null;
  }

  function getDiagramNodeDataArray(diagram) {
    if (diagram && diagram.model && Array.isArray(diagram.model.nodeDataArray)) {
      return diagram.model.nodeDataArray;
    }

    const nodeDataArray = [];
    if (diagram && diagram.nodes && typeof diagram.nodes.each === 'function') {
      diagram.nodes.each(node => {
        if (node && node.data) nodeDataArray.push(node.data);
      });
    }
    return nodeDataArray;
  }

  function selectDiagramParts(diagram, parts) {
    if (!diagram || !Array.isArray(parts) || parts.length === 0) return;
    diagram.clearSelection();
    parts.forEach(part => {
      if (part && part.selectable !== false) {
        part.isSelected = true;
      }
    });
  }

  function selectSingleDiagramPart(diagram, part) {
    selectDiagramParts(diagram, [part]);
  }

  function getEquipmentGroupedSelectionParts(diagram, equipmentNode) {
    const nodeDataArray = getDiagramNodeDataArray(diagram);
    const datasetKeys = getEquipmentDatasetKeysForSelection(equipmentNode && equipmentNode.data, nodeDataArray);
    const flagKeys = getEquipmentExternalFlagKeysForSelection(equipmentNode && equipmentNode.data, nodeDataArray);
    const parts = [equipmentNode];

    datasetKeys.concat(flagKeys).forEach(nodeKey => {
      const node = diagram.findNodeForKey(nodeKey);
      if (node) parts.push(node);
    });

    return parts;
  }

  function getPendingSelectedEquipmentRids() {
    if (!ctrl.selectedEquipmentRid) return [];
    const value = typeof ctrl.selectedEquipmentRid === 'function'
      ? ctrl.selectedEquipmentRid()
      : ctrl.selectedEquipmentRid;
    return (Array.isArray(value) ? value : [value]).filter(Boolean);
  }

  function clearPendingSelectedEquipmentRid() {
    if (typeof ctrl.selectedEquipmentRid === 'function') {
      ctrl.selectedEquipmentRid(null);
    } else {
      ctrl.selectedEquipmentRid = null;
    }
  }

  function selectPendingEquipmentGroup() {
    if (!isEquipmentDatasetSelectionEnabled(ctrl) || !ctrl.isEditingEnabled || !ctrl.isEditingEnabled() || !ctrl.diagram) return;

    const equipmentRids = getPendingSelectedEquipmentRids();
    if (equipmentRids.length === 0) return;

    const selectionParts = [];
    equipmentRids.forEach(equipmentRid => {
      const equipmentNode = ctrl.diagram.findNodeForKey(equipmentRid);
      if (!equipmentNode) return;

      const groupedParts = getEquipmentGroupedSelectionParts(ctrl.diagram, equipmentNode);
      selectionParts.push(...(groupedParts.length > 0 ? groupedParts : [equipmentNode]));
    });
    if (selectionParts.length === 0) return;

    selectDiagramParts(ctrl.diagram, selectionParts);
    ctrl.lastEquipmentDatasetSelectionKey = equipmentRids.length === 1 ? equipmentRids[0] : null;
    clearPendingSelectedEquipmentRid();
  }

  function shouldUseDefaultClickSelection(e) {
    const input = e && e.diagram && e.diagram.lastInput;
    return !!(input && (input.control || input.meta || input.shift));
  }

  function selectEquipmentDatasetsOnFirstClick(e) {
    if (!isEquipmentDatasetSelectionEnabled(ctrl) || !ctrl.isEditingEnabled || !ctrl.isEditingEnabled()) return;

    const diagram = e && e.diagram ? e.diagram : ctrl.diagram;
    const clickedNode = getClickedDiagramNode(e);

    if (!clickedNode || shouldUseDefaultClickSelection(e)) {
      ctrl.lastEquipmentDatasetSelectionKey = null;
      return;
    }

    if (isDatasetNodeData(clickedNode.data)) {
      selectSingleDiagramPart(diagram, clickedNode);
      ctrl.lastEquipmentDatasetSelectionKey = null;
      return;
    }

    if (!isEquipmentNodeDataForDatasetSelection(clickedNode.data)) {
      ctrl.lastEquipmentDatasetSelectionKey = null;
      return;
    }

    if (ctrl.lastEquipmentDatasetSelectionKey === clickedNode.data.key) {
      selectSingleDiagramPart(diagram, clickedNode);
      ctrl.lastEquipmentDatasetSelectionKey = null;
      return;
    }

    const selectionParts = getEquipmentGroupedSelectionParts(diagram, clickedNode);
    if (selectionParts.length <= 1) {
      selectSingleDiagramPart(diagram, clickedNode);
      ctrl.lastEquipmentDatasetSelectionKey = null;
      return;
    }

    selectDiagramParts(diagram, selectionParts);
    ctrl.lastEquipmentDatasetSelectionKey = clickedNode.data.key;
  }

  // Add listeners for both ObjectSingleClicked and BackgroundSingleClicked
  ctrl.diagram.addDiagramListener("ObjectSingleClicked", (e) => {
    enableScrolling();
    addDiagramBorder();
    selectEquipmentDatasetsOnFirstClick(e);
  });

  ctrl.diagram.addDiagramListener("BackgroundSingleClicked", () => {
    enableScrolling();
    addDiagramBorder();
    ctrl.lastEquipmentDatasetSelectionKey = null;
  });

  // Detect clicks outside the diagram
  ctrl.clickOutside = function (event) {
    if (diagramDiv && !diagramDiv.contains(event.target)) {
      disableScrolling();
      removeDiagramBorder();
    }
  }
  document.addEventListener("click", ctrl.clickOutside);

  ctrl.fullScreenChangeEvent = function () {
    const canvas = document.getElementById(ctrl.canvasId);
    if (document.fullscreenElement === canvas) {
      enableScrolling();
      addDiagramBorder();
    } else {
      disableScrolling();
      removeDiagramBorder();
    }
  }
  document.addEventListener("fullscreenchange", ctrl.fullScreenChangeEvent);

  ctrl.diagram.grid = $(go.Panel, go.Panel.Grid, {
    gridCellSize: new go.Size(diagramConfiguration.calculateCellWidth(), diagramConfiguration.calculateCellHeight()),
  }, ctrl.useGeoPositioning ?
    ''
    :
    $(go.Shape, "LineH", {
      strokeDashArray: [1, diagramConfiguration.calculateCellWidth() - 1],
      stroke: "gray"
    })
  );

  ctrl.linkAnimation;
  ctrl.updateLinkAnimation = function () {
    if (ctrl.linkAnimation) ctrl.linkAnimation.stop();
    if (ctrl.diagram && ctrl.diagram.links) {
      ctrl.diagram.skipsUndoManager = true;
      try {
        ctrl.diagram.startTransaction('Sanitize Link Points');
        ctrl.diagram.links.each(link => {
          sanitizeLiveLinkPoints(link, getGoLinkRouteKind(link));
        });
        ctrl.diagram.commitTransaction('Sanitize Link Points');
      } finally {
        ctrl.diagram.skipsUndoManager = false;
      }
    }
    // Animate the flow in the pipes
    ctrl.linkAnimation = new go.Animation();
    ctrl.linkAnimation.easing = go.Animation.EaseLinear;
    // ctrl.diagram.links.each(link => link.data.class !== 'Contains' && link.data.class !== 'Controls' ? ctrl.linkAnimation.add(link.findObject("PIPE"), "strokeDashOffset", 80, 0) : '');

    ctrl.linkPointsSubscribed = [];
    ctrl.diagram.links.each(link => {
      if (link.data.class !== 'Contains' && link.data.class !== 'Controls') {
        if (link.data.minValue && link.data.maxValue && link.data.formula && link.data.pointsLinked && link.data.pointsLinked.length > 0) {
          link.data.columns = link.data.pointsLinked.map(pointLinked => {
            return {
              in: {
                '@rid': pointLinked.rid,
                minValue: link.data.minValue,
                maxValue: link.data.maxValue,
                equipmentClass: 'Feeds',
                linkKey: link.data.key,
                formula: link.data.formula,
                pointsLinked: link.data.pointsLinked,
                value: '0'
              }
            }
          });
          link.data.columns.forEach(column => {
            if (column && column.in && column.in['@rid']) {
              if (
                !ctrl.diagram.model.modelData[`${link.data.key}_${column.in['@rid'].toString()}`] &&
                ctrl.diagram.model.modelData[`${link.data.key}_${column.in['@rid'].toString()}`] != 0
              ) {
                ctrl.diagram.model.commit(function (mod) {
                  mod.set(mod.modelData, `${link.data.key}_${column.in['@rid'].toString()}`, '0');
                });

                if (!ctrl.isEditingEnabled()) {
                  ctrl.pointSubscriberObj.subscribe({ column: column }, ctrl.diagram);
                }
              }
            }
          });
        }
      }
    });

    // Run indefinitely
    ctrl.linkAnimation.runCount = Infinity;
    ctrl.linkAnimation.start();
  }

  function canCreateLink(fromNode, fromPort, toNode, toPort) {
    return portConnectionRules.canCreateFeedsLink(
      fromNode,
      fromPort,
      toNode,
      toPort,
      measurableModel.measurableClassTree.categories
    );
  }

  // Create a custom LinkingTool
  ctrl.diagram.toolManager.linkingTool = new go.LinkingTool();
  ctrl.diagram.toolManager.linkingTool.linkValidation = canCreateLink;
  ctrl.diagram.toolManager.linkingTool.isEnabled = !ctrl.isDiagram;

  const snapGenericEquipmentBodyResize = (value, cellSize) => {
    if (!Number.isFinite(value) || !Number.isFinite(cellSize) || cellSize <= 0) return value;
    return Math.max(Math.round(value / cellSize) * cellSize, 1);
  };
  const baseComputeResize = ctrl.diagram.toolManager.resizingTool.computeResize;
  ctrl.diagram.toolManager.resizingTool.computeResize = function(newPoint, spot, min, max, cell, reshape) {
    const rect = baseComputeResize.call(this, newPoint, spot, min, max, cell, reshape);
    const part = this.adornedObject && this.adornedObject.part;
    if (!part || !isGridShellEquipmentData(part.data)) return rect;

    const width = snapGenericEquipmentBodyResize(rect.width, diagramConfiguration.calculateCellWidth());
    const height = snapGenericEquipmentBodyResize(rect.height, diagramConfiguration.calculateCellHeight());
    if (spot && spot.x === 0) rect.x += rect.width - width;
    if (spot && spot.y === 0) rect.y += rect.height - height;
    rect.width = width;
    rect.height = height;
    return rect;
  };

  // Create a custom dragging tool
  var customDraggingTool = new go.DraggingTool();
  customDraggingTool.isGridSnapEnabled = true; // Enable grid snapping
  customDraggingTool.isGridSnapRealtime = true; // Enable real-time grid snapping

  // Set the diagram to use the custom dragging tool
  ctrl.diagram.toolManager.draggingTool.doActivate = function () {
    if (ctrl.useGeoPositioning) {
      ctrl.map.dragging.disable();
    }
    go.DraggingTool.prototype.doActivate.call(this);
  };

  ctrl.diagram.toolManager.draggingTool.doDeactivate = function () {
    if (ctrl.useGeoPositioning) {
      ctrl.map.dragging.enable();
    }
    go.DraggingTool.prototype.doDeactivate.call(this);
  };

  ctrl.diagram.toolManager.draggingTool = customDraggingTool;

  const templmap = new go.Map();
  createNodeTemplates(templmap, ctrl.map, ctrl.useGeoPositioning, ctrl.submitCallback, ctrl.nodeEquipmentArray, ctrl.diagram);

  ctrl.diagram.nodeTemplateMap = templmap;

  // Define a default link template
  createLinkTemplates(ctrl.diagram.linkTemplateMap, ctrl.map, ctrl.useGeoPositioning, ctrl.submitCallback, { disableTopologyEdits: ctrl.isDiagram });

  function calculateScale(zoom, latitude) {
    const R = 6378137;
    const latRad = latitude * (Math.PI / 180);
    const latCircumference = Math.cos(latRad) * 2 * Math.PI * R;
    const totalPixels = 256 * Math.pow(2, zoom);
    return latCircumference / totalPixels;
  }

  ctrl.updateNodes = function () {
    updatingGoJs = true;
    const zoom = ctrl.map.getZoom();
    const scale = calculateScale(zoom, ctrl.map.getCenter().lat);
    const targetSizeMeters = 2;
    let targetSizePixels = targetSizeMeters / scale;
    const minSizePixels = 20;
    targetSizePixels = Math.max(targetSizePixels, minSizePixels);

    ctrl.diagram.commit(diag => {
      diag.nodes.each(n => {
        n.updateTargetBindings('latlong');
      });
      diag.links.each(l => l.updateTargetBindings("route"));
    }, null);
    updatingGoJs = false;
  }

  ctrl.updatePosition = function () {
    const mapb = ctrl.map.getBounds();
    const topLeftLatLng = [mapb.getNorth(), mapb.getWest()];
    const zoom = ctrl.map.getZoom();
    const pos = ctrl.map.project(topLeftLatLng, zoom);
    ctrl.diagram.position = new go.Point(pos.x, pos.y);
  }

  
  
  ctrl.linkFeedsDataArray.forEach(link => {  
    if (typeof link.fromPortId === 'number') {
      link.fromPortId = link.fromPortId.toString();
    }
    if (typeof link.toPortId === 'number') {
      link.toPortId = link.toPortId.toString();
    }
  });

  const diagramNodeKeys = new Set([
    ...ctrl.groupsArray.map(n => n.key),
    ...ctrl.nodeEquipmentArray.map(n => n.key),
    ...ctrl.nodePortArray.map(n => n.key),
    ...ctrl.nodeDirectDatasetArray.map(n => n.key),
    ...ctrl.nodeDatasetArray.map(n => n.key),
    ...ctrl.nodeLocationArray.map(n => n.key),
  ]);
  const connectedPortsByNodeKey = new Map();
  const portsToHideByNodeKey = new Map();
  const portDefinitionsByNodeKey = new Map();
  [...ctrl.nodeEquipmentArray, ...ctrl.nodeLocationArray].forEach(node => {
    if (node && node.key && node.ports) {
      portDefinitionsByNodeKey.set(node.key, node.ports);
    }
  });
  const normalizeNodeKey = (val) => {
    if (val === undefined || val === null) return null;
    if (typeof val === 'string') return val;
    if (typeof val === 'object' && val['@rid']) return val['@rid'];
    return null;
  };
  const addConnectedPortForNode = (nodeKeyRaw, portIdRaw, otherKeyRaw) => {
    const nodeKey = normalizeNodeKey(nodeKeyRaw);
    const otherKey = normalizeNodeKey(otherKeyRaw);
    if (!nodeKey) return;
    if (!diagramNodeKeys.has(nodeKey)) return;
    if (!otherKey) return;
    const portId = normalizeConnectionPortId(portIdRaw, portDefinitionsByNodeKey.get(nodeKey));
    if (!portId) return;
    if (!connectedPortsByNodeKey.has(nodeKey)) connectedPortsByNodeKey.set(nodeKey, new Set());
    connectedPortsByNodeKey.get(nodeKey).add(portId);
  };

  [...ctrl.linkControlsDataArray, ...ctrl.linkFeedsDataArray].forEach(link => {
    addConnectedPortForNode(link.from, link.fromPortId, link.to);
    addConnectedPortForNode(link.to, link.toPortId, link.from);
  });

  if (!ctrl.isEditingEnabled()) {
    const collectHiddenPortsForNode = (node) => {
      const connected = connectedPortsByNodeKey.get(node.key);
      const hidden = new Set();
      ['leftArray', 'rightArray', 'topArray', 'bottomArray', 'centerArray'].forEach(side => {
        if (Array.isArray(node[side])) {
          node[side].forEach(port => {
            const pid = normalizeConnectionPortId(port && port.portId, node.ports);
            if (!pid) return;
            if (!connected || !connected.has(pid)) {
              hidden.add(pid);
            }
          });
        }
      });
      if (hidden.size > 0) portsToHideByNodeKey.set(node.key, hidden);
    };
    ctrl.nodeEquipmentArray.forEach(collectHiddenPortsForNode);
    ctrl.nodeLocationArray.forEach(collectHiddenPortsForNode);
  }


  assignDefaultDiagramEquipmentLocations(ctrl);
  assignDefaultDatasetTileLocations(ctrl);
  assignDefaultExternalFlagLocations(ctrl);
  sanitizeDiagramModelCoordinates(ctrl);

  ctrl.diagram.model = new go.GraphLinksModel(
    [
      ...ctrl.groupsArray,
      ...ctrl.nodeEquipmentArray,
      ...ctrl.nodePortArray,
      ...ctrl.nodeDirectDatasetArray,
      ...ctrl.nodeDatasetArray,
      ...ctrl.nodeLocationArray,
    ],
    [
      ...ctrl.linkControlsDataArray,
      ...ctrl.linkFeedsDataArray,
    ],
    {
      linkFromPortIdProperty: "fromGoPortId",
      linkToPortIdProperty: "toGoPortId"
    }
  );

  if (!ctrl.isEditingEnabled() && portsToHideByNodeKey.size > 0) {
    const normalizeGraphPortId = (pid) => {
      if (pid === undefined || pid === null) return null;
      if (typeof pid === 'string') {
        if (pid.startsWith('Port_') || pid.startsWith('Controls_')) return pid;
        return `Port_${pid}`;
      }
      return `Port_${pid}`;
    };
    ctrl.diagram.nodes.each(node => {
      const hidden = portsToHideByNodeKey.get(node.data.key);
      if (!hidden) return;
      node.ports.each(port => {
        const pid = normalizeGraphPortId(port.portId);
        if (pid && hidden.has(pid)) {
          port.opacity = 0;
          port.fromLinkable = false;
          port.toLinkable = false;
        } else {
          port.opacity = 1;
        }
      });
    });
  }

  ctrl.waitForParts = function (ridsCreated, maxRetries = 30, interval = 500) {
    return new Promise((resolve, reject) => {
      let retries = 0;

      function checkParts() {
        let partsCreated = ridsCreated.map(item => {
          var part = ctrl.diagram.findPartForKey(item);
          return part !== null ? part : undefined;
        });
        if (partsCreated.some((part) => part !== undefined)) {
          // At least one part is found
          resolve(partsCreated.filter((part) => part !== undefined));
        } else if (retries >= maxRetries) {
          // Timeout reached
          reject(new Error('Timed out waiting for parts to be created.'));
        } else {
          // Retry
          retries++;
          setTimeout(checkParts, interval);
        }
      }

      checkParts();
    });
  }

  if (ctrl.forceReadOnly) {
    ctrl.diagram.isReadOnly = true;
    ctrl.diagram.allowSelect = false;
    ctrl.diagram.allowMove = false;
    ctrl.diagram.toolManager.panningTool.isEnabled = true;
    disableScrolling();
    ctrl.diagram.toolManager.contextMenuTool.isEnabled = false;
    return;
  }

  addEventsToDiagram(ctrl);

  if (!ctrl.isDiagram) {
    ctrl.diagram.toolManager.mouseDownTools.add(new GroupingTool(ctrl.diagram, ctrl.equipmentRecord(), ctrl.parentReinitializeAndRedraw));
  }

  const groupingButton = document.getElementById(ctrl.groupingButtonId);
  if (groupingButton) {
    groupingButton.onclick = function () {
      var tool = ctrl.diagram.toolManager.findTool("GroupingTool");
      if (tool) {
        tool.isActive = true;  // Set the tool as active
        tool.doActivate();     // Activate the tool
      }
    };
  }

}


var addEventsToDiagram = function (ctrl) {
  if (ctrl.useGeoPositioning) {
    ctrl.map.on("zoomend", ctrl.updateNodes);
    ctrl.map.on("move", ctrl.updatePosition);
    ctrl.map.on("moveend", ctrl.updatePosition);
  }

  const equipmentRecord = () => typeof ctrl.equipmentRecord === 'function' ? ctrl.equipmentRecord() : ctrl.equipmentRecord;
  const shouldBlockPaste = () => modal.vm.opened() || equipmentRecord()?.['@class'] === 'System' || equipmentRecord()?.['@class'] === 'Diagram' || ctrl.useGeoPositioning;
  const getLocation = (partData) => {
    if (partData.loc) return partData.loc;
    if (Array.isArray(partData.latlong) && partData.latlong.length === 2) return partData.latlong.join(' ');
    const diagramDataByEquipment = partData.diagramData && equipmentRecord() ? partData.diagramData[equipmentRecord()['@rid']] : undefined;
    if (diagramDataByEquipment && diagramDataByEquipment.loc) return diagramDataByEquipment.loc;
    return undefined;
  };
  const isResizableEquipmentPart = (part) => {
    if (!(part instanceof go.Node) || !part.data || !part.data.category) return false;
    if (['Group', 'Dataset', 'Port'].includes(part.data.category)) return false;
    return !!part.findObject('BODY');
  };
  const snapDiagramCoordinate = (value, cellSize) => {
    if (!Number.isFinite(value) || !Number.isFinite(cellSize) || cellSize <= 0) return value;
    return Math.round(value / cellSize) * cellSize;
  };
  const snapDiagramPoint = (point) => {
    return new go.Point(
      snapDiagramCoordinate(point.x, diagramConfiguration.calculateCellWidth()),
      snapDiagramCoordinate(point.y, diagramConfiguration.calculateCellHeight())
    );
  };
  const serializePartLocation = (part) => {
    if (ctrl.useGeoPositioning) {
      const latlong = ctrl.map.unproject(L.point(part.location.x, part.location.y), ctrl.map.getZoom());
      return `${latlong.lat.toFixed(6)} ${latlong.lng.toFixed(6)}`;
    }
    const snappedLocation = snapDiagramPoint(part.location);
    return `${snappedLocation.x.toFixed(2)} ${snappedLocation.y.toFixed(2)}`;
  };
  const normalizeResizableBodySizeForStorage = (part, width, height) => {
    if (!part) return { width, height };
    if (isRoomNodeData(part.data)) {
      return gridEquipmentNode.normalizeBodySizeForStorage(width, height, roomGridDefaults);
    }
    if (isGridShellEquipmentData(part.data)) {
      return gridEquipmentNode.normalizeBodySizeForStorage(width, height);
    }
    return { width, height };
  };
  const createNodeDiagramLayoutEntry = (nodeData, loc) => {
    const entry = { loc };
    if (nodeData.height !== undefined) entry.height = nodeData.height;
    if (nodeData.width !== undefined) entry.width = nodeData.width;
    if (isGridShellEquipmentData(nodeData) || isRoomNodeData(nodeData)) {
      if (nodeData.cols !== undefined) entry.cols = nodeData.cols;
      if (nodeData.rows !== undefined) entry.rows = nodeData.rows;
    }
    return entry;
  };
  const addConnectedLinkRoutes = (part, nodesAndLinksMoved) => {
    if (!part.linksConnected) return;
    part.linksConnected.each(function (link) {
      if (!link.data || !link.data.key) return;
      if (nodesAndLinksMoved.find(n => n['@rid'] == link.data.key)) return;

      let entry = {};
      const routeKind = getGoLinkRouteKind(link);
      if (ctrl.useGeoPositioning && link.points && typeof link.points.each === 'function') {
        const route = [];
        link.points.each(p => {
          const latLng = ctrl.map.unproject({ x: p.x, y: p.y }, ctrl.map.getZoom());
          route.push({ lat: latLng.lat, long: latLng.lng });
        });
        if (route.length === 0) return;
        entry.route = route;
      } else {
        const points = normalizeLiveDiagramLinkPoints(link, routeKind);
        if (points.length === 0) return;
        entry.points = points;
      }
      entry.routeKind = routeKind;

      nodesAndLinksMoved.push({
        '@rid': link.data.key,
        body: {
          diagramData: mergeDiagramDataEntry(link.data.diagramData, ctrl.equipmentRecord()['@rid'], entry)
        },
        category: link.data.class
      });
    });
  };
  const offsetLocation = (loc) => {
    if (!loc || typeof loc !== 'string') return undefined;
    const coords = loc.split(' ');
    const x = parseFloat(coords[0]);
    const y = parseFloat(coords[1]);
    if (isNaN(x) || isNaN(y)) return undefined;
    return `${50 + x} ${50 + y}`;
  };
  const getDataFields = (partData) => {
    if (partData && partData.data && Array.isArray(partData.data.fields)) {
      return partData.data.fields;
    }
    if (partData && Array.isArray(partData.fields)) {
      return partData.fields;
    }
    return [];
  };
  const getPartValue = (partData, key) => {
    if (partData && partData.data && partData.data[key] !== undefined) {
      return partData.data[key];
    }
    if (partData && partData[key] !== undefined) {
      return partData[key];
    }
    return undefined;
  };

  const buildNewNodesAndLinksFromParts = (parts) => {
    let newNodesAndLinks = [];
    parts.forEach(rawPart => {
      const partData = rawPart instanceof go.Part ? rawPart.data : rawPart;
      if (!partData) return;
      const category = partData.category;
      const partClass = partData.class;

      if (partClass === 'Feeds') {
        let newLink = {
          '@class': partClass,
          category: partData.category,
          diagramData: partData.diagramData,
          from: partData.from,
          fromPortId: partData.fromPortId,
          to: partData.to,
          toPortId: partData.toPortId,
        }
        newNodesAndLinks.push(newLink);
        return;
      }

      if (category === 'Port') {
        let newPort = {
          name: partData.name,
          category: partData.category,
          type: partData.type,
          key: partData.key,
          class: partClass,
        }
        const partLoc = offsetLocation(getLocation(partData));
        if (partLoc) {
          newPort.loc = partLoc;
        }
        if (partData.direction) {
          newPort.direction = partData.direction;
        }
        if (partData.connector) {
          newPort.connector = partData.connector;
        }
        newNodesAndLinks.push(newPort);
        return;
      }

      if (category === 'Dataset') {
        let newNode = {
          '@rid': partData.key,
          '@class': 'Dataset',
          columns: (partData.columns || []).map(column => {
            return {
              name: column.name,
              id: column.id,
              index: column.index,
            }
          }),
          definedByParentEquipmentClass: partData.definedByParentEquipmentClass == true,
          name: partData.name,
          parentRecordRid: partData.parentRecordRid,
          isDefaulDataset: partData.isDefaulDataset == true,
        }

        const partLoc = offsetLocation(getLocation(partData));
        if (partLoc) {
          newNode.loc = partLoc;
        }

        ontologyFields.normalizeFieldDefinitions(getDataFields(partData)).forEach(field => {
          const fieldName = ontologyFields.getFieldName(field);
          const fieldValue = getPartValue(partData, fieldName);
          if (ontologyFields.isDisplayableFieldValue(fieldValue, field)) {
            newNode[fieldName] = cloneJson(fieldValue);
          }
        });

        const ports = getPartValue(partData, 'ports');
        if (ports) {
          newNode.data = { ports: ports };
        }

        if (partData.datasetsRids && partData.datasetsRids.length > 0) {
          newNode.datasetsRids = partData.datasetsRids;
        }

        newNodesAndLinks.push(newNode);
        return;
      }

      if (partClass && category !== 'Dataset' && category !== 'Port') {
        let newNode = {
          '@rid': partData.key,
          '@class': partClass,
          category: 'Equipment',
          data: partData.data ? { ...partData.data } : {},
        }

        const partLoc = offsetLocation(getLocation(partData));
        if (partLoc) {
          newNode.loc = partLoc;
        }

        const svg = getPartValue(partData, 'svg');
        const svgConverted = getPartValue(partData, 'svgConverted');
        if (svg && svgConverted) {
          newNode.svg = svg;
          newNode.svgConverted = svgConverted;
        }

        const svgBackground = getPartValue(partData, 'svgBackground');
        const svgBackgroundConverted = getPartValue(partData, 'svgBackgroundConverted');
        if (svgBackground && svgBackgroundConverted) {
          newNode.svgBackground = svgBackground;
          newNode.svgBackgroundConverted = svgBackgroundConverted;
        }

        const svgForeground = getPartValue(partData, 'svgForeground');
        const svgForegroundConverted = getPartValue(partData, 'svgForegroundConverted');
        if (svgForeground && svgForegroundConverted) {
          newNode.svgForeground = svgForeground;
          newNode.svgForegroundConverted = svgForegroundConverted;
        }

        const rotatorDashArray = getPartValue(partData, 'rotatorDashArray');
        if (rotatorDashArray) {
          newNode.rotatorDashArray = rotatorDashArray;
        }
        const rotatorColor = getPartValue(partData, 'rotatorColor');
        if (rotatorColor) {
          newNode.rotatorColor = rotatorColor;
        }
        const hideRotator = getPartValue(partData, 'hideRotator');
        if (hideRotator) {
          newNode.hideRotator = hideRotator;
        }

        const ports = getPartValue(partData, 'ports');
        if (ports) {
          newNode.data.ports = ports;
        }

        if (partData.datasetsRids && partData.datasetsRids.length > 0) {
          newNode.datasetsRids = partData.datasetsRids;
        }

        ontologyFields.normalizeFieldDefinitions(getDataFields(partData)).forEach(field => {
          const fieldName = ontologyFields.getFieldName(field);
          const fieldValue = getPartValue(partData, fieldName);
          if (ontologyFields.isDisplayableFieldValue(fieldValue, field)) {
            newNode[fieldName] = cloneJson(fieldValue);
          }
        });

        Object.keys(customEquimentModel.fields || {}).forEach(key => {
          if (partData[key]) {
            newNode[key] = partData[key];
          }
        });

        newNodesAndLinks.push(newNode);
      }
    });

    return newNodesAndLinks;
  }

  const collectCreatedRidsFromPasteResponse = (response) => {
    if (!response) return [];
    if (response.tx && Array.isArray(response.tx.created)) {
      return response.tx.created.map(r => r && r['@rid']).filter(Boolean);
    }
    return [
      ...(Array.isArray(response.nodes) ? response.nodes : []),
      ...(Array.isArray(response.datasets) ? response.datasets : []),
      ...(Array.isArray(response.edges) ? response.edges : [])
    ].map(r => r && r['@rid']).filter(Boolean);
  };

  const handlePastedParts = function (parts) {
    if (!parts || parts.length === 0 || shouldBlockPaste() || pastingData || !ctrl.isEditingEnabled()) {
      return Promise.resolve();
    }
    pastingData = true;
    const newNodesAndLinks = buildNewNodesAndLinksFromParts(parts);
    if (newNodesAndLinks.length === 0) {
      pastingData = false;
      return Promise.resolve();
    }

    let ridsCreated;
    return loaders.requestWithLoader({
      loader: 'main',
      url: `/api/v1/customequipment/${ctrl.equipmentRecord()['@rid'].slice(1)}/pastediagram`,
      method: 'PUT',
      body: { newNodesAndLinks: newNodesAndLinks }
    }).then(function (response) {
      ridsCreated = collectCreatedRidsFromPasteResponse(response);
      return ctrl.parentReinitializeAndRedraw();
    })
      .then(function () {
        if (ridsCreated && ridsCreated.length > 0) {
          return ctrl.waitForParts(ridsCreated);
        }
        return [];
      })
      .then((partsCreated) => {
        if (partsCreated && partsCreated.length > 0) {
          ctrl.diagram.selectCollection(partsCreated);
        }
        pastingData = false;
      })
      .catch(function (error) {
        if (error.status == 400) {
          alert(`Error: ${error.message}`);
        } else {
          console.error("Clipboard paste error", error);
        }
        pastingData = false;
      });
  }

  const previousPasteEvent = ctrl.pasteEvent;
  ctrl.pasteEvent = function (e) {
    if (shouldBlockPaste()) return;
    e.preventDefault();
    if (navigator.clipboard && ctrl.isEditingEnabled()) {
      if (pastingData) return;
      const paste = e.clipboardData.getData("text");
      let parsedData;
      try {
        parsedData = JSON.parse(paste);
      } catch (error) {
        console.error("Failed to parse pasted JSON", error);
        return;
      }
      if (Array.isArray(parsedData) && parsedData.length > 0) {
        handlePastedParts(parsedData);
      }
    } else {
      const commandHandler = ctrl.diagram.commandHandler;
      if (commandHandler.canPasteSelection()) commandHandler.pasteSelection();
    }
  }

  if (previousPasteEvent) {
    document.removeEventListener('paste', previousPasteEvent);
  }
  document.addEventListener('paste', ctrl.pasteEvent);

  ctrl.diagram.addDiagramListener("ClipboardPasted", function (e) {
    const pastedParts = [];
    e.subject.each(function (part) {
      pastedParts.push(part);
    });

    pastedParts.forEach(part => {
      e.diagram.remove(part);
    });

    const partsToHandle = (ctrl.lastCopiedSelection && ctrl.lastCopiedSelection.length > 0)
      ? ctrl.lastCopiedSelection
      : pastedParts;
    handlePastedParts(partsToHandle);
  });

  function addExternalDiagramFlagMove(node, nodesAndLinksMoved) {
    if (!node || !node.data || !node.data.isExternalDiagramFlag) return false;

    let movedLink = null;
    if (node.linksConnected) {
      node.linksConnected.each(function (link) {
        if (movedLink || !link.data || !link.data.key) return;
        if (!node.data.externalFeedRid || link.data.key === node.data.externalFeedRid) {
          movedLink = link;
        }
      });
    }

    const feedRid = (movedLink && movedLink.data && movedLink.data.key) || node.data.externalFeedRid;
    if (!feedRid) return true;

    const diagramRid = ctrl.equipmentRecord()['@rid'];
    const entry = { routeKind: 'externalFlag' };
    if (ctrl.useGeoPositioning) {
      const latlong = ctrl.map.unproject(L.point(node.location.x, node.location.y), ctrl.map.getZoom());
      entry.externalFlagLoc = formatDiagramPoint({ x: latlong.lat, y: latlong.lng });
      if (movedLink && movedLink.points && typeof movedLink.points.each === 'function') {
        const route = [];
        movedLink.points.each(p => {
          const latLng = ctrl.map.unproject({ x: p.x, y: p.y }, ctrl.map.getZoom());
          route.push({ lat: latLng.lat, long: latLng.lng });
        });
        entry.route = route;
      }
    } else {
      const newPosition = node.location;
      const newPositionString = formatDiagramPoint({ x: newPosition.x, y: newPosition.y });
      node.data.loc = newPositionString;
      node.data.latlong = [newPosition.x, newPosition.y];
      entry.externalFlagLoc = newPositionString;
      if (movedLink) entry.points = normalizeLiveDiagramLinkPoints(movedLink, 'externalFlag');
    }

    const linkData = movedLink && movedLink.data ? movedLink.data : {};
    const diagramData = mergeDiagramDataEntry(linkData.diagramData, diagramRid, entry);
    const existingMove = nodesAndLinksMoved.find(n => n['@rid'] === feedRid);
    if (existingMove) {
      existingMove.body = existingMove.body || {};
      existingMove.body.diagramData = mergeDiagramDataEntry(existingMove.body.diagramData, diagramRid, entry);
      existingMove.category = 'Feeds';
    } else {
      nodesAndLinksMoved.push({
        '@rid': feedRid,
        body: { diagramData },
        category: 'Feeds'
      });
    }

    return true;
  }

  ctrl.diagram.addDiagramListener("SelectionMoved", function (e) {

    const movedNodes = e.subject; // List of moved nodes
    let nodesAndLinksMoved = [];
    if (ctrl.useGeoPositioning) {
      movedNodes.each(function (node) {
        if (!node.data.category) { return; }
        if (addExternalDiagramFlagMove(node, nodesAndLinksMoved)) {
          return;
        } else if (node.data.category == 'Port') {
          //TODO Ports
        } else if (node.data.category == 'Dataset') {
          const latlong = ctrl.map.unproject(L.point(node.location.x, node.location.y), ctrl.map.getZoom());
          let body = {};
          body.diagramData = {
            ...node.data.diagramData, [ctrl.equipmentRecord()['@rid']]: {
              height: node.data.height,
              width: node.data.width,
              loc: `${latlong.lat} ${latlong.lng}`
            }
          }

          nodesAndLinksMoved.push({
            '@rid': node.data.key,
            body,
            category: node.data.category == 'Dataset' ? 'Dataset' : (isRoomNodeData(node.data) ? 'Room' : 'Equipment'),
          });
        } else {
          const ll = ctrl.map.unproject(L.point(node.location.x, node.location.y), ctrl.map.getZoom());
          let nodeMoved = {
            '@rid': node.data.key,
            latitude: ll.lat,
            longitude: ll.lng
          }
          nodesAndLinksMoved.push(nodeMoved);
        }
        if (node.linksConnected) {
          node.linksConnected.each(function (link) {
            if (link.data.key) {
              if (!nodesAndLinksMoved.find(n => n['@rid'] == link.data.key)) {
                let body = {};
                const routeKind = getGoLinkRouteKind(link);
                body.diagramData = mergeDiagramDataEntry(link.data.diagramData, ctrl.equipmentRecord()['@rid'], {
                  points: normalizeLiveDiagramLinkPoints(link, routeKind),
                  routeKind: routeKind
                });
                nodesAndLinksMoved.push({
                  '@rid': link.data.key,
                  body,
                  category: link.data.class
                });
              }
            }
          });
        }
      });
      return loaders.requestWithLoader({
        loader: 'main',
        url: getMoveDiagramUrl(ctrl),
        method: 'PUT',
        body: { nodesAndLinksMoved: nodesAndLinksMoved }
      }).then(function (response) {
        ctrl.parentReinitializeAndRedraw();
      })
    } else {
      movedNodes.each(function (node) {
        const newPosition = snapDiagramPoint(node.location);
        const newPositionString = newPosition.x.toFixed(2) + " " + newPosition.y.toFixed(2)
        node.location = newPosition;
        node.data.loc = newPositionString;
        node.data.latlong = [newPosition.x, newPosition.y];
        if (!node.data.category) { return; }
        if (addExternalDiagramFlagMove(node, nodesAndLinksMoved)) {
          // External diagram flags are derived from feeds, so their placement is stored on the feed.
        } else if (node.data.category == 'Port') {
          let tempPortArray = Object.entries(ctrl.equipmentRecord().ports)
          tempPortArray.forEach(port => {
            if (port[1].name == node.data.name) {
              port[1].loc = newPositionString;
            }
          })
          let body = Object.fromEntries(tempPortArray);

          if (!nodesAndLinksMoved.find(n => n['@rid'] == ctrl.equipmentRecord()['@rid'])) {
            nodesAndLinksMoved.push({
              '@rid': ctrl.equipmentRecord()['@rid'],
              body,
              category: node.data.category
            });
          }

        } else if (node.data.category == 'Group') {
          let groups = node.data.parentRecordGroups;
          groups.forEach(group => {
            if (group.id == node.data.key) {
              group.loc = newPositionString;
              group.width = node.data.width;
              group.height = node.data.height;
            }
          });

          nodesAndLinksMoved.push({
            groups,
            category: node.data.category
          });
        } else {
          let body = {};
          body.diagramData = mergeDiagramDataEntry(
            node.data.diagramData,
            ctrl.equipmentRecord()['@rid'],
            createNodeDiagramLayoutEntry(node.data, node.data.loc)
          );

          nodesAndLinksMoved.push({
            '@rid': node.data.key,
            body,
            category: node.data.category == 'Dataset' ? 'Dataset' : (isRoomNodeData(node.data) ? 'Room' : 'Equipment'),
          });

        }
        if (node.linksConnected) {
          node.linksConnected.each(function (link) {
            if (link.data.key) {
              if (!nodesAndLinksMoved.find(n => n['@rid'] == link.data.key)) {
                let body = {};
                const routeKind = getGoLinkRouteKind(link);
                body.diagramData = mergeDiagramDataEntry(link.data.diagramData, ctrl.equipmentRecord()['@rid'], {
                  points: normalizeLiveDiagramLinkPoints(link, routeKind),
                  routeKind: routeKind
                });
                nodesAndLinksMoved.push({
                  '@rid': link.data.key,
                  body,
                  category: link.data.class
                });
              }
            }
          });
        }
      });

      return loaders.requestWithLoader({
        loader: 'main',
        url: getMoveDiagramUrl(ctrl),
        method: 'PUT',
        body: { nodesAndLinksMoved: nodesAndLinksMoved }
      }).then(function (response) {
        ctrl.parentReinitialize();
      })
    }
  });

  ctrl.getCenterPoint = function (nodeId) {
    // Assuming 'ctrl.diagram' is your GoJS diagram instance
    var nodeData = ctrl.diagram.model.findNodeDataForKey(nodeId);

    // Assuming your node data includes 'lat' and 'lng' properties for geographical coordinates
    if (nodeData && Array.isArray(nodeData.latlong)) {
      return { lat: nodeData.latlong[0], long: nodeData.latlong[1] };
    } else {
      // Handle cases where the geographical data is not available or invalid
      console.error(`Geographical position not found or invalid for node with key ${nodeId}`);
      return null; // Or return a default position, depending on your requirements
    }
  };

  ctrl.diagram.addDiagramListener("LinkReshaped", function (e) {
    if (ctrl.isDiagram) {
      if (!e.subject || !e.subject.data || e.subject.data.class !== 'Feeds') return;
      var diagramBody = {};
      if (ctrl.useGeoPositioning) {
        let latlngArray = [];
        if (e.subject.points && typeof e.subject.points.each === 'function') {
          e.subject.points.each(p => {
            const latLng = ctrl.map.unproject({ x: p.x, y: p.y }, ctrl.map.getZoom());
            latlngArray.push({ lat: latLng.lat, long: latLng.lng });
          });
        }
        diagramBody.diagramData = mergeDiagramDataEntry(e.subject.data.diagramData, ctrl.equipmentRecord()['@rid'], {
          route: latlngArray,
          routeKind: getGoLinkRouteKind(e.subject)
        });
      } else {
        const routeKind = getGoLinkRouteKind(e.subject);
        diagramBody.diagramData = mergeDiagramDataEntry(e.subject.data.diagramData, ctrl.equipmentRecord()['@rid'], {
          points: normalizeLiveDiagramLinkPoints(e.subject, routeKind),
          routeKind: routeKind
        });
      }
      return loaders.requestWithLoader({
        loader: 'main',
        url: getMoveDiagramUrl(ctrl),
        method: 'PUT',
        body: {
          nodesAndLinksMoved: [{
            '@rid': e.subject.data.key,
            body: diagramBody,
            category: 'Feeds'
          }]
        }
      }).then(function () {
        ctrl.parentReinitialize();
      });
    }

    if (e.subject.data.class == 'Feeds' || e.subject.data.class == 'Controls') {
      let toCenter = ctrl.getCenterPoint(e.subject.data.to);
      let fromCenter = ctrl.getCenterPoint(e.subject.data.from);

      var body = {};
      if (ctrl.useGeoPositioning) {
        let routeArray = e.subject.points.map(p => ctrl.map.unproject({ x: p.x, y: p.y }, ctrl.map.getZoom()));

        let latlngArray = routeArray.s.map(p => ({ lat: p.lat, long: p.lng }));
        latlngArray[0] = { lat: fromCenter.lat, long: fromCenter.long };
        latlngArray[routeArray.length - 1] = { lat: toCenter.lat, long: toCenter.long };
        // TODO: Add latitud and longitud of the two nodes conected to the latlngArray
        body.diagramData = mergeDiagramDataEntry(e.subject.data.diagramData, ctrl.equipmentRecord()['@rid'], {
          route: latlngArray,
          routeKind: getGoLinkRouteKind(e.subject)
        });
      } else {
        if (e.subject.data.from.includes('Port')) {
          body.fromNode = ctrl.equipmentRecord()['@rid'];
        } else {
          body.fromNode = e.subject.data.from;
        }
        if (e.subject.data.to.includes('Port')) {
          body.toNode = ctrl.equipmentRecord()['@rid'];
        } else {
          body.toNode = e.subject.data.to;
        }
        body.fromPortId = e.subject.data.fromPortId;
        body.toPortId = e.subject.data.toPortId;
        body.serviceClass = serviceTaxonomy.getServiceClass(e.subject.data);
        body.category = serviceTaxonomy.getServiceClass(e.subject.data);
        const routeKind = getGoLinkRouteKind(e.subject);
        body.diagramData = mergeDiagramDataEntry(e.subject.data.diagramData, ctrl.equipmentRecord()['@rid'], {
          points: normalizeLiveDiagramLinkPoints(e.subject, routeKind),
          routeKind: routeKind
        });
      }
      loaders.requestWithLoader({
        loader: 'main',
        url: `/api/v1/${e.subject.data.class.toLowerCase()}/${e.subject.data.key.slice(1)}`,
        method: 'PUT',
        body
      }).then(function (response) {
        ctrl.parentReinitialize();
      })
    }
  });

  ctrl.diagram.addDiagramListener("LinkDrawn", function (e) {
    var linkCreated = e.subject;
    if (ctrl.isDiagram) {
      if (linkCreated && linkCreated.data) {
        ctrl.diagram.model.removeLinkData(linkCreated.data);
      }
      return;
    }
    const linkServiceClass = getLinkCreatedServiceClass(linkCreated);
    let newLinkItem = {
      linkColor: diagramConfiguration.findPortColor(linkServiceClass),
      fromPortId: getGoPortId(linkCreated.fromPort),
      toPortId: getGoPortId(linkCreated.toPort),
    }
    let body;
    if (isControlsGoPort(linkCreated.fromPort)) {
      newLinkItem.fromNode = ctrl.diagram.findNodeForKey(linkCreated.fromNode.data.key);
      newLinkItem.toNode = ctrl.diagram.findNodeForKey(linkCreated.toNode.data.key);
      body = {
        fromPortId: 'Controls_0',
        fromNode: linkCreated.fromNode.data.key,
        toPortId: 'Controls_0',
        toNode: linkCreated.toNode.data.key,
        category: 'Controls',
      };

      return loaders.requestWithLoader({
        loader: 'main',
        url: `/api/v1/controls`,
        method: 'POST',
        body
      }).then(result => {
        let newLinkData = {
          key: result['@rid'],
          class: result['@class'],
          category: result.category,
          from: result.out,
          fromPortId: 'Controls_0',
          fromGoPortId: getGoPortId(linkCreated.fromPort),
          to: result.in,
          toPortId: 'Controls_0',
          toGoPortId: getGoPortId(linkCreated.toPort),
          curviness: 20,
          linkColor: controlsEdgeColor,
          labelText: 'Control'
        }
        ctrl.diagram.model.assignAllDataProperties(linkCreated.data, newLinkData);
        ctrl.parentReinitialize();
      });
    } else if (linkCreated.fromNode.data.category == 'Port') {
      newLinkItem.fromNode = ctrl.diagram.findNodeForKey(`${linkCreated.fromNode.key}`);
      newLinkItem.toNode = ctrl.diagram.findNodeForKey(linkCreated.toNode.data.key);
      body = {
        fromNode: ctrl.equipmentRecord()['@rid'],
        fromPortId: normalizeLinkPortId(linkCreated.fromNode.key),
        toNode: linkCreated.toNode.data.key,
        toPortId: normalizeLinkPortId(getGoPortId(linkCreated.toPort)),
        serviceClass: linkServiceClass,
        category: linkServiceClass
      }
    } else if (linkCreated.toNode.data.category == 'Port') {
      newLinkItem.fromNode = ctrl.diagram.findNodeForKey(linkCreated.fromNode.data.key);
      newLinkItem.toNode = ctrl.diagram.findNodeForKey(`${linkCreated.toNode.key}`);
      body = {
        fromNode: linkCreated.fromNode.data.key,
        fromPortId: normalizeLinkPortId(getGoPortId(linkCreated.fromPort)),
        toNode: ctrl.equipmentRecord()['@rid'],
        toPortId: normalizeLinkPortId(linkCreated.toNode.key),
        serviceClass: linkServiceClass,
        category: linkServiceClass
      }
    } else {
      newLinkItem.fromNode = ctrl.diagram.findNodeForKey(linkCreated.fromNode.data.key);
      newLinkItem.toNode = ctrl.diagram.findNodeForKey(linkCreated.toNode.data.key);
      body = {
        fromPortId: normalizeLinkPortId(getGoPortId(linkCreated.fromPort)),
        fromNode: linkCreated.fromNode.data.key,
        toPortId: normalizeLinkPortId(getGoPortId(linkCreated.toPort)),
        toNode: linkCreated.toNode.data.key,
        serviceClass: linkServiceClass,
        category: linkServiceClass
      }
    }

    return loaders.requestWithLoader({
      loader: 'main',
      url: `/api/v1/feeds`,
      method: 'POST',
      body
    }).then(result => {
      var fromNode;
      if (result.out == ctrl.equipmentRecord()['@rid']) {
        fromNode = `Port_${result.fromPortId}`;
      } else {
        fromNode = result.out;
      }

      let newLinkData = {
        key: result['@rid'],
        class: result['@class'],
        serviceClass: serviceTaxonomy.getServiceClass(result),
        category: serviceTaxonomy.getServiceClass(result),
        from: fromNode,
        fromPortId: result.fromPortId,
        fromGoPortId: getGoPortId(linkCreated.fromPort),
        to: result.in,
        toPortId: result.toPortId,
        toGoPortId: getGoPortId(linkCreated.toPort),
        linkColor: diagramConfiguration.findPortColor(serviceTaxonomy.getServiceClass(result)),
        routing: ctrl.useGeoPositioning ? go.Link.Normal : go.Link.Orthogonal
      }

      ctrl.diagram.model.assignAllDataProperties(linkCreated.data, newLinkData);
      ctrl.parentReinitialize();

    })
  });

  ctrl.diagram.addDiagramListener("SelectionDeleted", function (e) {
    var selection = e.subject;
    if (ctrl.isDiagram) {
      const equipmentNodeRids = new Set(ctrl.nodeEquipmentArray.map(node => node.key));
      const equipmentRidsToRemove = [];
      selection.each(function (part) {
        if (part instanceof go.Node && equipmentNodeRids.has(part.data.key)) {
          equipmentRidsToRemove.push(part.data.key);
        }
      });

      if (equipmentRidsToRemove.length === 0) {
        ctrl.parentReinitializeAndRedraw();
        return;
      }

      return Promise.map(equipmentRidsToRemove, function (equipmentRid) {
        return loaders.requestWithLoader({
          loader: 'main',
          url: `/api/v1/diagram/${ctrl.equipmentRecord()['@rid'].slice(1)}/equipment/${equipmentRid.slice(1)}`,
          method: 'DELETE'
        });
      }).then(function () {
        ctrl.parentReinitializeAndRedraw();
      });
    }
    let nodesAndLinksDeleted = [];
    selection.each(function (part) {
      if (part instanceof go.Node) {
        if (part.data.category == 'Port') {
          let ports = Object.entries(ctrl.equipmentRecord().ports)
          let body = Object.fromEntries(ports.filter(port => `Port_${port[1].id}` != part.data.key))

          if (!nodesAndLinksDeleted.find(n => n['@rid'] == ctrl.equipmentRecord()['@rid'])) {
            nodesAndLinksDeleted.push({
              '@rid': ctrl.equipmentRecord()['@rid'],
              body,
              category: part.data.category
            });
          } else {
            var tempBody = nodesAndLinksDeleted.find(n => n['@rid'] == ctrl.equipmentRecord()['@rid']).body;
            Object.keys(tempBody).forEach(key => {
              if (`Port_${tempBody[key].id}` === part.data.key) {
                delete tempBody[key];
              }
            });
          }
        } else if (part.data.category == 'Room') {
          nodesAndLinksDeleted.push({
            '@rid': part.data.key,
            category: part.data.category
          });
        } else if (part.data.category == 'Group') {
          nodesAndLinksDeleted.push({
            category: part.data.category,
            id: part.data.key
          });
        } else {
          (part.data.datasetsRids || []).forEach(datasetRid => {
            nodesAndLinksDeleted.push({
              '@rid': datasetRid,
              category: 'Dataset'
            });
            let datasetNode = ctrl.diagram.findNodeForKey(`${datasetRid}`);
            datasetNode ? ctrl.diagram.remove(datasetNode) : ''
          });

          nodesAndLinksDeleted.push({
            '@rid': part.data.key,
            category: 'Equipment'
          });
        }
      } else if (part instanceof go.Link) {
        if (part.data.class == 'Feeds') {
          nodesAndLinksDeleted.push({
            '@rid': part.data.key,
            category: part.data.class
          });
        } else if (part.data.class == 'Controls') {
          nodesAndLinksDeleted.push({
            '@rid': part.data.key,
            category: part.data.class
          });
        }
      }
    });
    deletePartModal.editDialogue(ctrl.equipmentRecord()['@rid'], nodesAndLinksDeleted, { submitCallback: ctrl.parentReinitializeAndRedraw, isSystem: ctrl.isSystem });
  });

  ctrl.diagram.addDiagramListener("PartResized", function (e) {
    var resizedObject = e.subject;
    var part = resizedObject instanceof go.Part ? resizedObject : resizedObject && resizedObject.part;
    if (part instanceof go.Node) {
      if (part.data.category == 'Group') {
        let groups = part.data.parentRecordGroups;
        groups.forEach(group => {
          if (group.id == part.data.key) {
            group.width = part.actualBounds.width;
            group.height = part.actualBounds.height;
            group.loc = `${part.location.x} ${part.location.y}`;
          }
        });
        return loaders.requestWithLoader({
          loader: 'main',
          url: `/api/v1/customequipment/${ctrl.equipmentRecord()['@rid'].slice(1)}/groups`,
          method: 'PUT',
          body: groups
        }).then(function (response) {
          ctrl.parentReinitializeAndRedraw();
        })
      } else if (isResizableEquipmentPart(part)) {
        const storageObject = part.findObject('BODY');
        const rawWidth = Number((storageObject && storageObject.actualBounds ? storageObject.actualBounds.width : part.actualBounds.width).toFixed(2));
        const rawHeight = Number((storageObject && storageObject.actualBounds ? storageObject.actualBounds.height : part.actualBounds.height).toFixed(2));
        const normalizedBodySize = normalizeResizableBodySizeForStorage(part, rawWidth, rawHeight);
        const width = normalizedBodySize.width;
        const height = normalizedBodySize.height;
        const cols = normalizedBodySize.cols;
        const rows = normalizedBodySize.rows;
        if (!ctrl.useGeoPositioning) {
          part.location = snapDiagramPoint(part.location);
        }
        const loc = serializePartLocation(part);
        const layoutEntry = {
          height,
          width,
          loc
        };
        if (cols !== undefined) layoutEntry.cols = cols;
        if (rows !== undefined) layoutEntry.rows = rows;
        const diagramData = mergeDiagramDataEntry(part.data.diagramData, ctrl.equipmentRecord()['@rid'], layoutEntry);
        const nodesAndLinksMoved = [{
          '@rid': part.data.key,
          body: { diagramData },
          category: isRoomNodeData(part.data) ? 'Room' : 'Equipment'
        }];

        part.data.width = width;
        part.data.height = height;
        if (cols !== undefined) part.data.cols = cols;
        if (rows !== undefined) part.data.rows = rows;
        part.data.loc = loc;
        part.data.diagramData = diagramData;
        addConnectedLinkRoutes(part, nodesAndLinksMoved);

        return loaders.requestWithLoader({
          loader: 'main',
          url: getMoveDiagramUrl(ctrl),
          method: 'PUT',
          body: { nodesAndLinksMoved }
        }).then(function () {
          ctrl.parentReinitializeAndRedraw();
        });
      }
    }
  });
}

equipmentDiagram.view = function () {
  var ctrl = this;
  return m('div.h-100.w-100.mt1.absolute', [
    m('div.f4.pb2', {
      id: ctrl.groupingButtonId,
      style: {
        width: 'fit-content',
    display: ctrl.isEditingEnabled() && !ctrl.isDiagram ? 'block' : 'none',
      },
    }, [
      m('div.ba.b--blue.b--dashed.bw1.ph1.h2.flex.items-center', {
        onclick: () => {
          var tool = ctrl.diagram.toolManager.findTool("GroupingTool");
          if (tool) {
            tool.isActive = true;  // Set the tool as active
            ctrl.diagram.currentTool = tool;  // Set the current tool to the grouping tool
          }
        }
      }, [
        m('div.flex.justify-center.blue', [
          m('i.ion-plus.mr2'),
          m('div', 'Draw Group')
        ])
      ])
    ]),
    m('div', {
      style: {
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
      },
      id: ctrl.canvasId
    }),

  ])
}

equipmentDiagram.onupdate = function (vnode) {
  var ctrl = this;
  ctrl.enableEquipmentDatasetSelection = vnode.attrs.enableEquipmentDatasetSelection === true;
  if (ctrl.equipmentRecordRid != vnode.attrs.equipmentRecord()['@rid']) {
    ctrl.diagram.div = null;
    ctrl.diagram = null;
    ctrl.pointSubscriberObj.unsubscribeAll();
    ctrl.init();
  }
  if (ctrl.redrawDiagram != vnode.attrs.redrawDiagram()) {
    vnode.attrs.redrawDiagram(false);
    ctrl.diagram.div = null;
    ctrl.diagram = null;
    ctrl.pointSubscriberObj.unsubscribeAll();
    ctrl.init();
  }
  if (ctrl.editing() != ctrl.isEditingEnabled()) {
    ctrl.editing(ctrl.isEditingEnabled());
    createLinkTemplates(ctrl.diagram.linkTemplateMap, ctrl.map, ctrl.useGeoPositioning, ctrl.submitCallback, { disableTopologyEdits: ctrl.isDiagram });
    ctrl.diagram.div = null;
    ctrl.diagram = null;
    ctrl.pointSubscriberObj.unsubscribeAll();
    ctrl.init();
  }

}
equipmentDiagram.onremove = function (vnode) {
  var ctrl = this;
  ctrl.pointSubscriberObj.unsubscribeAll();
  ctrl.diagram.div = null;
  ctrl.diagram = null;
  document.removeEventListener('paste', ctrl.pasteEvent);
  document.removeEventListener('fullscreenchange', ctrl.fullScreenChangeEvent);
  document.removeEventListener('click', ctrl.clickOutside);
}

equipmentDiagram._private = {
  addDiagramFeedLinks,
  assignDefaultDiagramEquipmentLocations,
  assignDefaultDatasetTileLocations,
  assignDefaultExternalFlagLocations,
  applyDefaultGridShellSizeForPorts,
  calculateExternalFlagLayout,
  estimateExternalFlagSize,
  hasStoredDiagramLocation,
  isSafeDiagramCoordinate,
  alignFeedLinkEndpointsToPorts,
  getPortConnectionPoint,
  isSafeGoPoint,
  normalizeNearlyStraightLinkPoints,
  normalizeStoredRoute,
  parseDiagramPoint,
  applyStoredRoute,
  sanitizeDiagramModelCoordinates,
  isEquipmentDatasetSelectionEnabled,
  isEquipmentPartDataForClipboard,
  getEquipmentDatasetKeysForSelection,
  getEquipmentExternalFlagKeysForSelection,
  shouldIncludeDirectDatasetInDiagram,
  shouldIncludeParentDatasetInDiagram,
  getDatasetDiagramVisibility,
  isDefaultDatasetForDiagram
};

module.exports = equipmentDiagram;
